import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Tabs,
  Tab,
  TextField,
  Button,
  Divider,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Collapse,
  Avatar,
} from '@mui/material';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  AreaChart,
  Area,
} from 'recharts';
import {
  CalendarToday as CalendarIcon,
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  Receipt as ReceiptIcon,
  Language as LanguageIcon,
  Repeat as RepeatIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  Cancel as CancelIcon,
  Edit as EditIcon,
  Payment as PaymentIcon,
  Person as PersonIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
} from '@mui/icons-material';
import { collection, query, where, getDocs, Timestamp, orderBy, limit, doc, deleteDoc, updateDoc, addDoc } from 'firebase/firestore';
import { db } from '../services/firebase';
import { useAuth } from '../contexts/AuthContext';
import { Menu } from '../types';
import { isReceiptDeleteProtected, RECEIPT_PROTECTION_MESSAGE } from '../utils/receiptProtection';
import { format, startOfDay, endOfDay, subDays, subMonths, startOfMonth, endOfMonth, eachDayOfInterval, parseISO } from 'date-fns';
import { ja } from 'date-fns/locale';
import { getBusinessDateRange, getBusinessDate, getBusinessDayRange , resolveBoundaryHour } from '../utils/businessDate';
import { TZDate } from '@date-fns/tz';
import { stripUndefined } from '../utils/firestoreSafe';
import VoidSlipDialog from '../components/VoidSlipDialog';
import { VOID_REASON_LABELS, type VoidReasonCode, type VoidTarget } from '../utils/slipVoid';

// Types
interface CancelInfo {
  staffId?: string;
  staffName?: string;
  reason?: string;
  cancelledAt?: Date;
}

interface SalesStaffData {
  staffId: string;
  staffName: string;
  shareRate: number;
  backRate?: number;
  backAmount?: number;
}

interface ItemBackInfo {
  staffId: string;
  staffName: string;
  backRate: number;
  backAmount: number;
  confirmedAt?: Date;
  confirmedBy?: string;
}

interface ItemCancelInfo {
  cancelledAt: Date;
  cancelledBy: string;
  reason: string;
  isHardDelete?: boolean;  // true = 完全削除（ログなし）
}

interface OrderItem {
  productId: string;
  productName: string;
  quantity: number;
  price?: number;
  subtotal?: number;
  costInfo?: {
    cost: number;
    baseBackRate: number;
  };
  backInfo?: ItemBackInfo;  // 商品単位のバック情報
  cancelInfo?: ItemCancelInfo;  // 商品単位のキャンセル情報
}

interface OrderData {
  id: string;
  shopId: string;
  tableId?: string;
  tableName?: string;
  tableNumber?: string;
  total: number;
  subtotal?: number;
  tax?: number;
  serviceCharge?: number;
  orderedAt: Date;
  completedAt?: Date;
  status: string;
  paymentMethod?: string;
  items: OrderItem[];
  customerId?: string;
  customerName?: string;
  customerPhone?: string;
  lineUserId?: string;
  lineDisplayName?: string;
  linePictureUrl?: string;
  language?: string;
  guestCount?: number;
  cancelInfo?: CancelInfo;
  salesStaff?: SalesStaffData[];
}

interface SalesAllocation {
  subtotal: number;
  totalCost: number;
  netSales: number;
  allocations: {
    staffId: string;
    staffName: string;
    type: 'percent' | 'amount';
    value: number;
    resolved: number;
  }[];
}

interface CheckoutData {
  id: string;
  shopId: string;
  tableId: string;
  tableName?: string;
  slipNumber?: string | null;
  businessDate?: string;
  subtotal: number;
  tax: number;
  serviceCharge: number;
  total: number;
  paymentMethod: string;
  paymentMethodName?: string;
  status: string;
  createdAt: Date;
  completedAt?: Date;
  guestCount?: number;
  orders: string[];
  paidBy?: {
    staffId: string;
    staffName: string;
  };
  salesAllocation?: SalesAllocation;
  salesStatus?: 'pending' | 'confirmed';
  confirmedBy?: string;
  confirmedAt?: Date;
}

interface POSCheckoutData {
  id: string;
  shopId: string;
  type: string;
  customerName: string;
  items: {
    menuId: string;
    menuName: string;
    price: number;
    quantity: number;
    category: string;
  }[];
  subtotal: number;
  discount?: {
    type: string;
    value: number;
    amount: number;
    reason?: string;
  };
  total: number;
  paymentMethod: string;
  receivedAmount: number;
  change: number;
  salesStaff?: SalesStaffData[];
  salesAllocation?: SalesAllocation;
  salesStatus?: 'pending' | 'confirmed';
  confirmedBy?: string;
  confirmedAt?: Date;
  createdAt: Date;
}

interface CustomerData {
  id: string;
  shopId: string;
  name?: string;
  phoneNumber?: string;
  language?: string;
  visitCount: number;
  totalSpent: number;
  lastVisitAt?: Date;
  createdAt: Date;
}

interface ProductSales {
  productId: string;
  productName: string;
  totalQuantity: number;
  totalRevenue: number;
  orderCount: number;
}

interface DailySales {
  date: string;
  displayDate: string;
  revenue: number;
  orderCount: number;
  guestCount: number;
  averagePerGuest: number;
}

interface HourlySales {
  hour: string;
  revenue: number;
  orderCount: number;
}

interface StaffSalesData {
  staffId: string;
  staffName: string;
  totalSales: number;
  orderCount: number;
}

interface StaffDailySales {
  date: string;
  displayDate: string;
  [staffId: string]: number | string;
}

type ABCRank = 'A' | 'B' | 'C';

interface ABCProduct extends ProductSales {
  rank: ABCRank;
  revenuePercentage: number;
  cumulativePercentage: number;
}

interface LanguageStats {
  language: string;
  displayName: string;
  orderCount: number;
  revenue: number;
  percentage: number;
  [key: string]: string | number;
}

interface RepeatCustomerStats {
  newCustomers: number;
  repeatCustomers: number;
  repeatRate: number;
  visitFrequency: { visits: string; count: number }[];
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const LANGUAGE_NAMES: { [key: string]: string } = {
  ja: '日本語',
  en: 'English',
  th: 'ไทย',
  'zh-TW': '繁體中文',
  ko: '한국어',
};

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82ca9d', '#ffc658'];

const Analytics: React.FC = () => {
  const { shop, user, currentRole } = useAuth();
  const canDeleteOrders = currentRole === 'owner' || currentRole === 'manager';
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Date range
  // 既定は過去1ヶ月。夜職は開店前に開くことが多く、"本日" だと
  // まだ営業していないので全部0になり、壊れているように見える。
  const [dateRangeType, setDateRangeType] = useState<'today' | 'week' | 'month' | 'custom'>('month');
  const [startDate, setStartDate] = useState<string>(format(new Date(), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState<string>(format(new Date(), 'yyyy-MM-dd'));

  // Data
  const [orders, setOrders] = useState<OrderData[]>([]);
  const [checkouts, setCheckouts] = useState<CheckoutData[]>([]);
  const [posCheckouts, setPosCheckouts] = useState<POSCheckoutData[]>([]);
  const [customers, setCustomers] = useState<CustomerData[]>([]);
  const [menus, setMenus] = useState<Menu[]>([]);

  // Computed statistics
  const [dailySales, setDailySales] = useState<DailySales[]>([]);
  const [hourlySales, setHourlySales] = useState<HourlySales[]>([]);
  const [productSales, setProductSales] = useState<ProductSales[]>([]);
  const [abcProducts, setAbcProducts] = useState<ABCProduct[]>([]);
  const [languageStats, setLanguageStats] = useState<LanguageStats[]>([]);
  const [repeatStats, setRepeatStats] = useState<RepeatCustomerStats | null>(null);
  const [staffSalesData, setStaffSalesData] = useState<StaffSalesData[]>([]);
  const [staffDailySales, setStaffDailySales] = useState<StaffDailySales[]>([]);
  const [selectedStaffId, setSelectedStaffId] = useState<string>('all');
  // 顧客要望(D): 支払方法別フィルタ ('all' | 'cash' | 'non_cash' | <code>)
  const [paymentMethodFilter, setPaymentMethodFilter] = useState<string>('all');

  // Summary stats
  const [totalRevenue, setTotalRevenue] = useState(0);
  const [totalOrders, setTotalOrders] = useState(0);
  const [totalGuests, setTotalGuests] = useState(0);
  const [totalGrossProfit, setTotalGrossProfit] = useState(0);
  const [totalCommission, setTotalCommission] = useState(0);

  // 伝票一覧ダイアログ用
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [dailyOrdersDialogOpen, setDailyOrdersDialogOpen] = useState(false);
  const [expandedCheckoutId, setExpandedCheckoutId] = useState<string | null>(null);
  const [expandedOrderId, setExpandedOrderId] = useState<string | null>(null);

  // 削除ダイアログ用
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [orderToDelete, setOrderToDelete] = useState<OrderData | null>(null);
  const [checkoutToDelete, setCheckoutToDelete] = useState<CheckoutData | null>(null);
  // 伝票の取消は、記録つきのモーダルを通す。
  // 記録の無い削除を作らないため、こちらを唯一の入口にする。
  const [voidTarget, setVoidTarget] = useState<VoidTarget | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isOrderHardDelete, setIsOrderHardDelete] = useState(false);
  const [orderLongPressTimer, setOrderLongPressTimer] = useState<NodeJS.Timeout | null>(null);
  const [orderDeleteReason, setOrderDeleteReason] = useState('');
  // 完全削除の最終確認モーダル用
  const [hardDeleteConfirmOpen, setHardDeleteConfirmOpen] = useState(false);
  const [pendingHardDelete, setPendingHardDelete] = useState<(() => Promise<void>) | null>(null);

  // バック率編集ダイアログ用
  const [editBackRateDialogOpen, setEditBackRateDialogOpen] = useState(false);
  const [editingOrder, setEditingOrder] = useState<OrderData | null>(null);
  const [editingSalesStaff, setEditingSalesStaff] = useState<SalesStaffData[]>([]);
  const [isSavingBackRate, setIsSavingBackRate] = useState(false);

  // 売上確定・配分編集用
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirmTarget, setConfirmTarget] = useState<{ type: 'checkout' | 'pos'; id: string } | null>(null);
  const [isConfirming, setIsConfirming] = useState(false);
  const [editAllocationDialogOpen, setEditAllocationDialogOpen] = useState(false);
  const [editAllocTarget, setEditAllocTarget] = useState<{ type: 'checkout' | 'pos'; id: string } | null>(null);
  const [editAllocations, setEditAllocations] = useState<SalesAllocation['allocations']>([]);
  const [editAllocNetSales, setEditAllocNetSales] = useState(0);
  const [isSavingAllocation, setIsSavingAllocation] = useState(false);

  // 商品単位バック確定用
  const [staff, setStaff] = useState<{ id: string; name: string }[]>([]);
  const [editItemBackDialogOpen, setEditItemBackDialogOpen] = useState(false);
  const [editingItemOrder, setEditingItemOrder] = useState<OrderData | null>(null);
  const [editingItemIndex, setEditingItemIndex] = useState<number>(-1);
  const [editingItemBackInfo, setEditingItemBackInfo] = useState<{
    staffId: string;
    staffName: string;
    backRate: number;
  }>({ staffId: '', staffName: '', backRate: 50 });

  // 商品削除用
  const [deleteItemDialogOpen, setDeleteItemDialogOpen] = useState(false);
  const [deleteItemOrder, setDeleteItemOrder] = useState<OrderData | null>(null);
  const [deleteItemIndex, setDeleteItemIndex] = useState<number>(-1);
  const [deleteItemReason, setDeleteItemReason] = useState('');
  const [isHardDelete, setIsHardDelete] = useState(false);
  const [longPressTimer, setLongPressTimer] = useState<NodeJS.Timeout | null>(null);
  const [isLongPressing, setIsLongPressing] = useState(false);

  const [averageOrderValue, setAverageOrderValue] = useState(0);
  const [averagePerGuest, setAveragePerGuest] = useState(0);

  // Update date range based on type
  useEffect(() => {
    const now = new Date();
    switch (dateRangeType) {
      case 'today':
        setStartDate(format(now, 'yyyy-MM-dd'));
        setEndDate(format(now, 'yyyy-MM-dd'));
        break;
      case 'week':
        setStartDate(format(subDays(now, 7), 'yyyy-MM-dd'));
        setEndDate(format(now, 'yyyy-MM-dd'));
        break;
      case 'month':
        setStartDate(format(subMonths(now, 1), 'yyyy-MM-dd'));
        setEndDate(format(now, 'yyyy-MM-dd'));
        break;
      // 'custom' - keep current values
    }
  }, [dateRangeType]);

  // Fetch data when date range changes
  useEffect(() => {
    if (shop && startDate && endDate) {
      fetchAllData();
    }
  }, [shop, startDate, endDate]);

  // Calculate statistics when data changes
  useEffect(() => {
    if (orders.length > 0 || checkouts.length > 0 || posCheckouts.length > 0) {
      calculateStatistics();
    }
  }, [orders, checkouts, posCheckouts, customers, menus]);

  // Fetch staff list
  useEffect(() => {
    const fetchStaff = async () => {
      if (!shop) return;
      try {
        const staffRef = collection(db, 'shops', shop.id, 'staff');
        const staffSnapshot = await getDocs(staffRef);
        const staffData = staffSnapshot.docs.map(doc => ({
          id: doc.id,
          name: doc.data().name || doc.data().displayName || 'スタッフ',
        }));
        setStaff(staffData);
      } catch (error) {
        console.error('Error fetching staff:', error);
      }
    };
    fetchStaff();
  }, [shop]);

  const fetchAllData = async () => {
    if (!shop) return;

    setLoading(true);
    setError(null);

    try {
      const tz = shop.timezone || 'Asia/Tokyo';
      const boundary = resolveBoundaryHour(shop);
      const { start, end } = getBusinessDateRange(startDate, endDate, tz, boundary);

      // Fetch orders
      const ordersRef = collection(db, 'orders');
      const ordersQuery = query(
        ordersRef,
        where('shopId', '==', shop.id),
        where('orderedAt', '>=', Timestamp.fromDate(start)),
        where('orderedAt', '<=', Timestamp.fromDate(end))
      );
      const ordersSnapshot = await getDocs(ordersQuery);
      const ordersData: OrderData[] = ordersSnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          shopId: data.shopId,
          tableId: data.tableId,
          tableName: data.tableName,
          tableNumber: data.tableNumber,
          slipNumber: data.slipNumber ?? null,
          businessDate: data.businessDate ?? '',
          total: data.total || 0,
          subtotal: data.subtotal,
          tax: data.tax,
          serviceCharge: data.serviceCharge,
          orderedAt: data.orderedAt?.toDate() || new Date(),
          completedAt: data.completedAt?.toDate(),
          status: data.status,
          paymentMethod: data.paymentMethod,
          items: data.items || [],
          customerId: data.customerId,
          customerName: data.customerName,
          customerPhone: data.customerPhone,
          lineUserId: data.lineUserId,
          lineDisplayName: data.lineDisplayName,
          linePictureUrl: data.linePictureUrl,
          language: data.language,
          guestCount: data.guestCount,
          cancelInfo: data.cancelInfo ? {
            staffId: data.cancelInfo.staffId,
            staffName: data.cancelInfo.staffName,
            reason: data.cancelInfo.reason,
            cancelledAt: data.cancelInfo.cancelledAt?.toDate(),
          } : undefined,
          salesStaff: data.salesStaff || undefined,
        };
      });
      setOrders(ordersData);

      // Fetch checkouts
      const checkoutsRef = collection(db, 'checkouts');
      const checkoutsQuery = query(
        checkoutsRef,
        where('shopId', '==', shop.id),
        where('createdAt', '>=', Timestamp.fromDate(start)),
        where('createdAt', '<=', Timestamp.fromDate(end))
      );
      const checkoutsSnapshot = await getDocs(checkoutsQuery);
      const checkoutsData: CheckoutData[] = checkoutsSnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          shopId: data.shopId,
          tableId: data.tableId,
          tableName: data.tableName,
          // 取消のときに「どの伝票が消えたか」を出すために要る。
          // 写していなかったため、取消モーダルの伝票番号と営業日が空だった。
          slipNumber: data.slipNumber ?? null,
          businessDate: data.businessDate ?? '',
          subtotal: data.subtotal || 0,
          tax: data.tax || 0,
          serviceCharge: data.serviceCharge || 0,
          total: data.total || 0,
          paymentMethod: data.paymentMethod || 'cash',
          paymentMethodName: data.paymentMethodName,
          status: data.status,
          createdAt: data.createdAt?.toDate() || new Date(),
          completedAt: data.completedAt?.toDate(),
          guestCount: data.guestCount,
          orders: data.orders || [],
          paidBy: data.paidBy ? {
            staffId: data.paidBy.staffId,
            staffName: data.paidBy.staffName,
          } : undefined,
          salesAllocation: data.salesAllocation || undefined,
          salesStatus: data.salesStatus || undefined,
          confirmedBy: data.confirmedBy || undefined,
          confirmedAt: data.confirmedAt?.toDate() || undefined,
        };
      });
      setCheckouts(checkoutsData);

      // Fetch POS checkouts
      const posCheckoutsRef = collection(db, 'posCheckouts');
      const posCheckoutsQuery = query(
        posCheckoutsRef,
        where('shopId', '==', shop.id),
        where('createdAt', '>=', Timestamp.fromDate(start)),
        where('createdAt', '<=', Timestamp.fromDate(end))
      );
      const posCheckoutsSnapshot = await getDocs(posCheckoutsQuery);
      const posCheckoutsData: POSCheckoutData[] = posCheckoutsSnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          shopId: data.shopId,
          type: data.type || 'pos',
          customerName: data.customerName || '匿名',
          items: data.items || [],
          subtotal: data.subtotal || 0,
          discount: data.discount,
          total: data.total || 0,
          paymentMethod: data.paymentMethod || 'cash',
          receivedAmount: data.receivedAmount || 0,
          change: data.change || 0,
          salesStaff: data.salesStaff,
          salesAllocation: data.salesAllocation || undefined,
          salesStatus: data.salesStatus || undefined,
          confirmedBy: data.confirmedBy || undefined,
          confirmedAt: data.confirmedAt?.toDate() || undefined,
          createdAt: data.createdAt?.toDate() || new Date(),
        };
      });
      setPosCheckouts(posCheckoutsData);

      // Fetch customers for repeat analysis
      const customersRef = collection(db, 'shops', shop.id, 'customers');
      const customersSnapshot = await getDocs(customersRef);
      const customersData: CustomerData[] = customersSnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          shopId: shop.id,
          name: data.name,
          phoneNumber: data.phoneNumber,
          language: data.language,
          visitCount: data.visitCount || 1,
          totalSpent: data.totalSpent || 0,
          lastVisitAt: data.lastVisitAt?.toDate(),
          createdAt: data.createdAt?.toDate() || new Date(),
        };
      });
      setCustomers(customersData);

      // Fetch menus for cost/commission analysis
      const menusRef = collection(db, 'menus');
      const menusQuery = query(
        menusRef,
        where('shopId', '==', shop.id)
      );
      const menusSnapshot = await getDocs(menusQuery);
      const menusData: Menu[] = menusSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate(),
      })) as Menu[];
      setMenus(menusData);

    } catch (err: any) {
      console.error('データ取得エラー:', err);
      setError('データの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const calculateStatistics = () => {
    // Use checkouts for revenue if available, otherwise use orders
    const completedCheckouts = checkouts.filter(c => c.status === 'completed');
    // paid ステータスも会計済みとして集計対象に追加
    const completedOrders = orders.filter(o => ['completed', 'served', 'paid'].includes(o.status));

    // Total revenue and counts
    // モバイルオーダー/テーブル会計の売上
    let revenue = completedCheckouts.length > 0
      ? completedCheckouts.reduce((sum, c) => sum + c.total, 0)
      : completedOrders.reduce((sum, o) => sum + o.total, 0);

    // POS会計の売上を追加
    const posRevenue = posCheckouts.reduce((sum, p) => sum + p.total, 0);
    revenue += posRevenue;

    const orderCount = completedOrders.length + posCheckouts.length;
    let guestCount = completedCheckouts.reduce((sum, c) => sum + (c.guestCount || 0), 0)
      || completedOrders.reduce((sum, o) => sum + (o.guestCount || 1), 0);
    // POS会計は1人として集計
    guestCount += posCheckouts.length;

    setTotalRevenue(revenue);
    setTotalOrders(orderCount);
    setTotalGuests(guestCount);
    setAverageOrderValue(orderCount > 0 ? revenue / orderCount : 0);
    setAveragePerGuest(guestCount > 0 ? revenue / guestCount : 0);

    // Calculate gross profit and commission based on menu costSettings
    let grossProfit = 0;
    let commission = 0;

    // Create a map of menu id to cost settings for quick lookup
    const menuCostMap = new Map<string, Menu['costSettings']>();
    menus.forEach(menu => {
      if (menu.costSettings) {
        menuCostMap.set(menu.id, menu.costSettings);
      }
    });

    // Calculate based on order items
    completedOrders.forEach(order => {
      order.items.forEach(item => {
        const itemRevenue = item.subtotal || (item.price ? item.price * item.quantity : 0);
        const costSettings = menuCostMap.get(item.productId);

        if (costSettings) {
          if (costSettings.type === 'fixed_cost' && costSettings.fixedCost) {
            // 固定原価の場合: 粗利 = 売上 - (原価 × 数量)
            const itemGrossProfit = itemRevenue - (costSettings.fixedCost * item.quantity);
            grossProfit += itemGrossProfit;
          } else if (costSettings.type === 'commission_rate' && costSettings.commissionRate) {
            // コミッション率の場合: コミッション = 売上 × 率 / 100
            const itemCommission = itemRevenue * costSettings.commissionRate / 100;
            commission += itemCommission;
          }
        }
      });
    });

    setTotalGrossProfit(grossProfit);
    setTotalCommission(commission);

    // Daily sales
    calculateDailySales(completedOrders, completedCheckouts);

    // Hourly sales
    calculateHourlySales(completedOrders);

    // Product sales
    calculateProductSales(completedOrders);

    // Language stats
    calculateLanguageStats(completedOrders);

    // Repeat customer stats
    calculateRepeatStats();

    // Staff sales
    calculateStaffSales(completedOrders);
  };

  const calculateStaffSales = (completedOrders: OrderData[]) => {
    const tz = shop?.timezone || 'Asia/Tokyo';
    const boundary = resolveBoundaryHour(shop);
    const start = parseISO(startDate);
    const end = parseISO(endDate);
    const days = eachDayOfInterval({ start, end });

    const staffMap = new Map<string, { staffName: string; totalSales: number; orderCount: number }>();
    const dailyStaffMap = new Map<string, Map<string, number>>(); // date -> (staffId -> sales)

    // Initialize daily map
    days.forEach(day => {
      dailyStaffMap.set(format(day, 'yyyy-MM-dd'), new Map());
    });

    const processStaffSales = (salesStaff: SalesStaffData[] | undefined, total: number, createdAt: Date) => {
      const bizDate = getBusinessDate(createdAt, tz, boundary);
      const dateKey = format(bizDate, 'yyyy-MM-dd');

      if (salesStaff && salesStaff.length > 0) {
        salesStaff.forEach(staff => {
          const staffAmount = total * (staff.shareRate / 100);
          const existing = staffMap.get(staff.staffId) || { staffName: staff.staffName, totalSales: 0, orderCount: 0 };
          existing.totalSales += staffAmount;
          existing.orderCount += 1;
          staffMap.set(staff.staffId, existing);

          const dayMap = dailyStaffMap.get(dateKey);
          if (dayMap) {
            dayMap.set(staff.staffId, (dayMap.get(staff.staffId) || 0) + staffAmount);
          }
        });
      } else {
        const unassigned = staffMap.get('__unassigned__') || { staffName: '未割当', totalSales: 0, orderCount: 0 };
        unassigned.totalSales += total;
        unassigned.orderCount += 1;
        staffMap.set('__unassigned__', unassigned);

        const dayMap = dailyStaffMap.get(dateKey);
        if (dayMap) {
          dayMap.set('__unassigned__', (dayMap.get('__unassigned__') || 0) + total);
        }
      }
    };

    // Process orders
    completedOrders.forEach(order => {
      processStaffSales(order.salesStaff, order.total, order.orderedAt);
    });

    // Process POS checkouts
    posCheckouts.forEach(pos => {
      processStaffSales(pos.salesStaff, pos.total, pos.createdAt);
    });

    // Build staff sales array (sorted by totalSales desc)
    const staffArray: StaffSalesData[] = Array.from(staffMap.entries())
      .map(([staffId, data]) => ({ staffId, ...data }))
      .sort((a, b) => b.totalSales - a.totalSales);

    setStaffSalesData(staffArray);

    // Build daily sales array for chart
    const allStaffIds = staffArray.map(s => s.staffId);
    const dailyArray: StaffDailySales[] = days.map(day => {
      const dateKey = format(day, 'yyyy-MM-dd');
      const displayDate = format(day, 'M/d');
      const dayMap = dailyStaffMap.get(dateKey) || new Map();
      const entry: StaffDailySales = { date: dateKey, displayDate };
      allStaffIds.forEach(id => {
        entry[id] = dayMap.get(id) || 0;
      });
      return entry;
    });

    setStaffDailySales(dailyArray);
  };

  const calculateDailySales = (ordersData: OrderData[], checkoutsData: CheckoutData[]) => {
    const tz = shop?.timezone || 'Asia/Tokyo';
    const boundary = resolveBoundaryHour(shop);
    const start = parseISO(startDate);
    const end = parseISO(endDate);
    const days = eachDayOfInterval({ start, end });

    const salesByDate = new Map<string, { revenue: number; count: number; guests: number }>();

    // Initialize all days
    days.forEach(day => {
      salesByDate.set(format(day, 'yyyy-MM-dd'), { revenue: 0, count: 0, guests: 0 });
    });

    // Aggregate from checkouts or orders (ステータスフィルター適用)
    const completedCheckoutsData = checkoutsData.filter(c => c.status === 'completed');
    const completedOrdersData = ordersData.filter(o => ['completed', 'served', 'paid'].includes(o.status));

    if (completedCheckoutsData.length > 0) {
      completedCheckoutsData.forEach(checkout => {
        const dateKey = getBusinessDate(checkout.createdAt, tz, boundary);
        const existing = salesByDate.get(dateKey) || { revenue: 0, count: 0, guests: 0 };
        salesByDate.set(dateKey, {
          revenue: existing.revenue + checkout.total,
          count: existing.count + 1,
          guests: existing.guests + (checkout.guestCount || 0),
        });
      });
    } else {
      completedOrdersData.forEach(order => {
        const dateKey = getBusinessDate(order.orderedAt, tz, boundary);
        const existing = salesByDate.get(dateKey) || { revenue: 0, count: 0, guests: 0 };
        salesByDate.set(dateKey, {
          revenue: existing.revenue + order.total,
          count: existing.count + 1,
          guests: existing.guests + (order.guestCount || 1),
        });
      });
    }

    // POS会計も日別売上に追加
    posCheckouts.forEach(posCheckout => {
      const dateKey = getBusinessDate(posCheckout.createdAt, tz, boundary);
      const existing = salesByDate.get(dateKey) || { revenue: 0, count: 0, guests: 0 };
      salesByDate.set(dateKey, {
        revenue: existing.revenue + posCheckout.total,
        count: existing.count + 1,
        guests: existing.guests + 1, // POS会計は1人として集計
      });
    });

    const dailySalesData: DailySales[] = Array.from(salesByDate.entries())
      .map(([date, data]) => ({
        date,
        displayDate: format(parseISO(date), 'M/d', { locale: ja }),
        revenue: data.revenue,
        orderCount: data.count,
        guestCount: data.guests,
        averagePerGuest: data.guests > 0 ? data.revenue / data.guests : 0,
      }))
      .sort((a, b) => a.date.localeCompare(b.date));

    setDailySales(dailySalesData);
  };

  const calculateHourlySales = (ordersData: OrderData[]) => {
    const hourlyMap = new Map<number, { revenue: number; count: number }>();

    // Initialize all hours
    for (let i = 0; i < 24; i++) {
      hourlyMap.set(i, { revenue: 0, count: 0 });
    }

    const tz = shop?.timezone || 'Asia/Tokyo';

    ordersData.forEach(order => {
      const hour = new TZDate(order.orderedAt, tz).getHours();
      const existing = hourlyMap.get(hour) || { revenue: 0, count: 0 };
      hourlyMap.set(hour, {
        revenue: existing.revenue + order.total,
        count: existing.count + 1,
      });
    });

    // POS会計も時間帯別売上に追加
    posCheckouts.forEach(posCheckout => {
      const hour = new TZDate(posCheckout.createdAt, tz).getHours();
      const existing = hourlyMap.get(hour) || { revenue: 0, count: 0 };
      hourlyMap.set(hour, {
        revenue: existing.revenue + posCheckout.total,
        count: existing.count + 1,
      });
    });

    const hourlyData: HourlySales[] = Array.from(hourlyMap.entries())
      .map(([hour, data]) => ({
        hour: `${hour}:00`,
        revenue: data.revenue,
        orderCount: data.count,
      }));

    setHourlySales(hourlyData);
  };

  const calculateProductSales = (ordersData: OrderData[]) => {
    const productMap = new Map<string, ProductSales>();

    ordersData.forEach(order => {
      order.items.forEach(item => {
        const itemRevenue = item.subtotal || (item.price ? item.price * item.quantity : 0);
        const existing = productMap.get(item.productId) || {
          productId: item.productId,
          productName: item.productName,
          totalQuantity: 0,
          totalRevenue: 0,
          orderCount: 0,
        };
        productMap.set(item.productId, {
          ...existing,
          totalQuantity: existing.totalQuantity + item.quantity,
          totalRevenue: existing.totalRevenue + itemRevenue,
          orderCount: existing.orderCount + 1,
        });
      });
    });

    // POS会計の商品も追加
    posCheckouts.forEach(posCheckout => {
      posCheckout.items.forEach(item => {
        const itemRevenue = item.price * item.quantity;
        const existing = productMap.get(item.menuId) || {
          productId: item.menuId,
          productName: item.menuName,
          totalQuantity: 0,
          totalRevenue: 0,
          orderCount: 0,
        };
        productMap.set(item.menuId, {
          ...existing,
          totalQuantity: existing.totalQuantity + item.quantity,
          totalRevenue: existing.totalRevenue + itemRevenue,
          orderCount: existing.orderCount + 1,
        });
      });
    });

    const productsData = Array.from(productMap.values())
      .sort((a, b) => b.totalRevenue - a.totalRevenue);
    setProductSales(productsData);

    // ABC Analysis
    const totalProductRevenue = productsData.reduce((sum, p) => sum + p.totalRevenue, 0);
    let cumulativeRevenue = 0;
    const abcData: ABCProduct[] = productsData.map(product => {
      const revenuePercentage = totalProductRevenue > 0
        ? (product.totalRevenue / totalProductRevenue) * 100
        : 0;
      cumulativeRevenue += revenuePercentage;

      let rank: ABCRank;
      if (cumulativeRevenue <= 70) rank = 'A';
      else if (cumulativeRevenue <= 90) rank = 'B';
      else rank = 'C';

      return {
        ...product,
        rank,
        revenuePercentage,
        cumulativePercentage: cumulativeRevenue,
      };
    });
    setAbcProducts(abcData);
  };

  const calculateLanguageStats = (ordersData: OrderData[]) => {
    const langMap = new Map<string, { count: number; revenue: number }>();

    ordersData.forEach(order => {
      const lang = order.language || 'ja';
      const existing = langMap.get(lang) || { count: 0, revenue: 0 };
      langMap.set(lang, {
        count: existing.count + 1,
        revenue: existing.revenue + order.total,
      });
    });

    const totalOrders = ordersData.length;
    const stats: LanguageStats[] = Array.from(langMap.entries())
      .map(([lang, data]) => ({
        language: lang,
        displayName: LANGUAGE_NAMES[lang] || lang,
        orderCount: data.count,
        revenue: data.revenue,
        percentage: totalOrders > 0 ? (data.count / totalOrders) * 100 : 0,
      }))
      .sort((a, b) => b.orderCount - a.orderCount);

    setLanguageStats(stats);
  };

  const calculateRepeatStats = () => {
    if (customers.length === 0) {
      setRepeatStats(null);
      return;
    }

    const newCustomers = customers.filter(c => c.visitCount === 1).length;
    const repeatCustomers = customers.filter(c => c.visitCount > 1).length;
    const total = customers.length;

    // Visit frequency distribution
    const frequencyMap = new Map<string, number>();
    customers.forEach(c => {
      let category: string;
      if (c.visitCount === 1) category = '1回';
      else if (c.visitCount <= 3) category = '2-3回';
      else if (c.visitCount <= 5) category = '4-5回';
      else if (c.visitCount <= 10) category = '6-10回';
      else category = '11回以上';

      frequencyMap.set(category, (frequencyMap.get(category) || 0) + 1);
    });

    const visitFrequency = ['1回', '2-3回', '4-5回', '6-10回', '11回以上'].map(visits => ({
      visits,
      count: frequencyMap.get(visits) || 0,
    }));

    setRepeatStats({
      newCustomers,
      repeatCustomers,
      repeatRate: total > 0 ? (repeatCustomers / total) * 100 : 0,
      visitFrequency,
    });
  };

  const formatCurrency = (value: number): string => {
    const currency = shop?.currency || 'JPY';
    const symbol = currency === 'THB' ? '฿' : currency === 'USD' ? '$' : '¥';
    return `${symbol}${Math.round(value).toLocaleString()}`;
  };

  const getRankColor = (rank: ABCRank): string => {
    switch (rank) {
      case 'A': return '#4caf50';
      case 'B': return '#ff9800';
      case 'C': return '#f44336';
    }
  };

  const exportCSV = () => {
    // Create CSV data
    const headers = ['日付', '売上', '注文数', '来店人数', '客単価'];
    const rows = dailySales.map(d => [
      d.date,
      d.revenue,
      d.orderCount,
      d.guestCount,
      Math.round(d.averagePerGuest),
    ]);

    const csvContent = [headers, ...rows]
      .map(row => row.join(','))
      .join('\n');

    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `sales_report_${startDate}_${endDate}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  // 日付クリックで伝票一覧を表示
  const handleDateClick = (date: string) => {
    setSelectedDate(date);
    setDailyOrdersDialogOpen(true);
    setExpandedCheckoutId(null);
    setExpandedOrderId(null);
  };

  // 会計に紐づく注文を取得
  const getOrdersForCheckout = (checkout: CheckoutData): OrderData[] => {
    if (!checkout.orders || checkout.orders.length === 0) {
      // ordersが空の場合はtableIdで当日の注文を探す
      const targetDate = checkout.createdAt;
      const dayStart = startOfDay(targetDate);
      const dayEnd = endOfDay(targetDate);
      return orders.filter(order => {
        const orderDate = order.orderedAt;
        return (
          orderDate >= dayStart &&
          orderDate <= dayEnd &&
          order.tableId === checkout.tableId
        );
      });
    }
    return orders.filter(order => checkout.orders.includes(order.id));
  };

  // 営業日ベースの日付範囲を取得するヘルパー
  const getBusinessDayRangeForDate = (date: string) => {
    const tz = shop?.timezone || 'Asia/Tokyo';
    const boundary = resolveBoundaryHour(shop);
    return getBusinessDayRange(date, tz, boundary);
  };

  // 選択した日の注文を取得（営業日ベース、キャンセル除外）
  const getOrdersForDate = (date: string): OrderData[] => {
    if (!date) return [];
    const { start, end } = getBusinessDayRangeForDate(date);

    return orders.filter(order => {
      if (!['completed', 'served', 'paid'].includes(order.status)) return false;
      return order.orderedAt >= start && order.orderedAt < end;
    }).sort((a, b) => b.orderedAt.getTime() - a.orderedAt.getTime());
  };

  // 選択した日の会計を取得（営業日ベース）
  const getCheckoutsForDate = (date: string): CheckoutData[] => {
    if (!date) return [];
    const { start, end } = getBusinessDayRangeForDate(date);

    return checkouts.filter(checkout => {
      return checkout.createdAt >= start && checkout.createdAt < end;
    }).sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
  };

  // 選択した日のPOS会計を取得（営業日ベース）
  const getPosCheckoutsForDate = (date: string): POSCheckoutData[] => {
    if (!date) return [];
    const { start, end } = getBusinessDayRangeForDate(date);

    return posCheckouts.filter(pos => {
      return pos.createdAt >= start && pos.createdAt < end;
    }).sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
  };

  // ステータスの日本語表示
  const getStatusLabel = (status: string): string => {
    const statusMap: { [key: string]: string } = {
      pending: '受付中',
      confirmed: '確認済',
      preparing: '調理中',
      ready: '提供準備完了',
      served: '提供済',
      completed: '会計済',
      cancelled: 'キャンセル',
    };
    return statusMap[status] || status;
  };

  // ステータスの色
  const getStatusColor = (status: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    const colorMap: { [key: string]: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' } = {
      pending: 'warning',
      confirmed: 'info',
      preparing: 'info',
      ready: 'primary',
      served: 'success',
      completed: 'success',
      cancelled: 'error',
    };
    return colorMap[status] || 'default';
  };

  // 支払い方法の日本語表示
  const getPaymentMethodLabel = (method?: string): string => {
    const methodMap: { [key: string]: string } = {
      cash: '現金',
      credit_card: 'クレジットカード',
      card: 'カード',
      qr: 'QR決済',
      other: 'その他',
    };
    return method ? (methodMap[method] || method) : '-';
  };

  // 注文削除ダイアログを開く
  const handleOpenDeleteDialog = (order: OrderData, e: React.MouseEvent) => {
    e.stopPropagation(); // 親要素のクリックイベントを止める
    setOrderToDelete(order);
    setCheckoutToDelete(null);
    setIsOrderHardDelete(false);
    setDeleteDialogOpen(true);
  };

  // 伝票長押し開始（3秒で完全削除）
  const handleOrderDeleteLongPressStart = (order: OrderData, e: React.MouseEvent | React.TouchEvent) => {
    e.stopPropagation();
    setOrderToDelete(order);
    setCheckoutToDelete(null);
    const timer = setTimeout(() => {
      setIsOrderHardDelete(true);
      setDeleteDialogOpen(true);
      setOrderLongPressTimer(null);
    }, 3000);
    setOrderLongPressTimer(timer);
  };

  // 伝票長押し終了
  const handleOrderDeleteLongPressEnd = () => {
    if (orderLongPressTimer) {
      clearTimeout(orderLongPressTimer);
      setOrderLongPressTimer(null);
    }
  };

  // 会計伝票の取消。記録つきのモーダルを直接開く。
  // 以前は確認モーダルを1つ挟んでから消していたが、
  // そこには「伝票番号がどうなるか」も「バックがどう動くか」も出ていなかった。
  const handleOpenDeleteCheckoutDialog = (checkout: CheckoutData, e: React.MouseEvent) => {
    e.stopPropagation();
    setCheckoutToDelete(checkout);
    const orders = getOrdersForCheckout(checkout);
    setVoidTarget({
      id: checkout.id,
      type: 'checkout',
      slipNumber: (checkout as any).slipNumber ?? null,
      total: checkout.total ?? 0,
      itemCount: orders.reduce((n, o: any) => n + (o.items?.length ?? 0), 0),
      businessDate: (checkout as any).businessDate ?? '',
      checkoutAt: checkout.createdAt,
      receiptIssued: orders.some((o: any) => o.receiptIssuedAt || o.receiptNumber),
      afterDailyClose: false,
      backAmount: 0,
    });
  };

  // 注文削除処理
  const handleDeleteOrder = async () => {
    if (!orderToDelete || !shop || !user || !canDeleteOrders) return;

    // 顧客要望⑥: 領収書発行済み伝票の削除を保護
    if (
      isReceiptDeleteProtected(orderToDelete as any, {
        shop,
        userRole: currentRole,
      })
    ) {
      alert(RECEIPT_PROTECTION_MESSAGE);
      return;
    }

    setIsDeleting(true);
    try {
      if (isOrderHardDelete) {
        // 長押し: 完全削除（物理削除）
        await deleteDoc(doc(db, 'orders', orderToDelete.id));
        // ローカルの状態を更新
        setOrders(prev => prev.filter(o => o.id !== orderToDelete.id));
      } else {
        // 通常クリック: 論理削除（マイナス伝票扱い）
        await updateDoc(doc(db, 'orders', orderToDelete.id), {
          status: 'cancelled',
          cancelledAt: Timestamp.now(),
          cancelledBy: user.uid,
          cancelReason: orderDeleteReason || '削除',
          isCancelled: true,
        });
        // ローカルの状態を更新
        setOrders(prev => prev.map(o =>
          o.id === orderToDelete.id
            ? { ...o, status: 'cancelled' }
            : o
        ));
      }

      // ダイアログを閉じる
      setDeleteDialogOpen(false);
      setOrderToDelete(null);
      setIsOrderHardDelete(false);
      setOrderDeleteReason('');

      // 統計を再計算
      fetchAllData();
    } catch (error) {
      console.error('Error deleting order:', error);
      alert('削除に失敗しました: ' + (error as Error).message);
    } finally {
      setIsDeleting(false);
    }
  };

  // 会計削除処理（会計と紐づく注文を全て削除）
  const handleDeleteCheckout = async (voidInput: { reasonCode: VoidReasonCode; note: string }) => {
    if (!checkoutToDelete || !shop || !user || !canDeleteOrders) return;

    // 顧客要望⑥: 領収書発行済み伝票（注文）を含む会計の削除を保護
    const checkoutOrders = getOrdersForCheckout(checkoutToDelete);
    const protectedOrder = checkoutOrders.find((o) =>
      isReceiptDeleteProtected(o as any, { shop, userRole: currentRole })
    );
    if (protectedOrder) {
      alert(RECEIPT_PROTECTION_MESSAGE);
      return;
    }

    setIsDeleting(true);
    try {
      // 取消の記録を先に残す。
      // 先に消してしまうと、記録に失敗したときに
      // 「誰がなぜ消したか分からない欠番」だけが残る。
      await addDoc(collection(db, 'slipVoids'), stripUndefined({
        shopId: shop.id,
        targetType: 'checkout',
        targetId: checkoutToDelete.id,
        slipNumber: (checkoutToDelete as any).slipNumber ?? null,
        businessDate: (checkoutToDelete as any).businessDate ?? '',
        originalTotal: checkoutToDelete.total ?? 0,
        originalItemCount: checkoutOrders.reduce((n, o: any) => n + (o.items?.length ?? 0), 0),
        reasonCode: voidInput.reasonCode,
        reasonLabel: VOID_REASON_LABELS[voidInput.reasonCode],
        note: voidInput.note,
        // 番号は詰めない。欠番として残す。
        numberKept: true,
        voidedBy: user.uid,
        voidedByName: (user as any).displayName ?? '',
        voidedByRole: currentRole ?? '',
        voidedAt: Timestamp.now(),
        createdAt: Timestamp.now(),
      }));

      // 注文を全て削除
      for (const order of checkoutOrders) {
        await deleteDoc(doc(db, 'orders', order.id));
      }

      // 会計を削除
      await deleteDoc(doc(db, 'checkouts', checkoutToDelete.id));

      // ローカルの状態を更新
      setOrders(prev => prev.filter(o => !checkoutOrders.some(co => co.id === o.id)));
      setCheckouts(prev => prev.filter(c => c.id !== checkoutToDelete.id));

      // ダイアログを閉じる
      setDeleteDialogOpen(false);
      setCheckoutToDelete(null);

      // 統計を再計算
      fetchAllData();
    } catch (error) {
      console.error('Error deleting checkout:', error);
      alert('削除に失敗しました: ' + (error as Error).message);
    } finally {
      setIsDeleting(false);
    }
  };

  // バック率編集ダイアログを開く
  const handleOpenEditBackRateDialog = (order: OrderData, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingOrder(order);
    setEditingSalesStaff(order.salesStaff ? [...order.salesStaff] : []);
    setEditBackRateDialogOpen(true);
  };

  // バック率保存処理
  const handleSaveBackRate = async () => {
    if (!editingOrder || !shop) return;

    setIsSavingBackRate(true);
    try {
      const orderRef = doc(db, 'orders', editingOrder.id);
      await import('firebase/firestore').then(({ updateDoc, Timestamp }) =>
        updateDoc(orderRef, {
          salesStaff: editingSalesStaff.map(s => ({
            ...s,
            backAmount: calculateStaffBackAmount(editingOrder, s),
          })),
          updatedAt: Timestamp.now(),
        })
      );

      // ローカルの状態を更新
      setOrders(prev => prev.map(o =>
        o.id === editingOrder.id
          ? { ...o, salesStaff: editingSalesStaff }
          : o
      ));

      setEditBackRateDialogOpen(false);
      setEditingOrder(null);
      setEditingSalesStaff([]);
    } catch (error) {
      console.error('Error saving back rate:', error);
      alert('保存に失敗しました: ' + (error as Error).message);
    } finally {
      setIsSavingBackRate(false);
    }
  };

  // スタッフのバック金額を計算
  const calculateStaffBackAmount = (order: OrderData, staff: SalesStaffData): number => {
    const backRate = staff.backRate ?? shop?.costBackSettings?.defaultBackRate ?? 0.5;
    const orderTotal = order.total || 0;
    return Math.round(orderTotal * staff.shareRate * backRate);
  };

  // 売上確定ダイアログを開く
  const handleOpenConfirmDialog = (type: 'checkout' | 'pos', id: string) => {
    setConfirmTarget({ type, id });
    setConfirmDialogOpen(true);
  };

  // 売上確定処理
  const handleConfirmSales = async () => {
    if (!confirmTarget || !user) return;
    setIsConfirming(true);
    try {
      const collectionName = confirmTarget.type === 'checkout' ? 'checkouts' : 'posCheckouts';
      const docRef = doc(db, collectionName, confirmTarget.id);
      await updateDoc(docRef, {
        salesStatus: 'confirmed',
        confirmedBy: user.uid,
        confirmedAt: Timestamp.now(),
      });

      // ローカル状態を更新
      if (confirmTarget.type === 'checkout') {
        setCheckouts(prev => prev.map(c =>
          c.id === confirmTarget.id ? { ...c, salesStatus: 'confirmed', confirmedBy: user.uid, confirmedAt: new Date() } : c
        ));
      } else {
        setPosCheckouts(prev => prev.map(p =>
          p.id === confirmTarget.id ? { ...p, salesStatus: 'confirmed', confirmedBy: user.uid, confirmedAt: new Date() } : p
        ));
      }
      setConfirmDialogOpen(false);
      setConfirmTarget(null);
    } catch (error) {
      console.error('Error confirming sales:', error);
      alert('確定に失敗しました: ' + (error as Error).message);
    } finally {
      setIsConfirming(false);
    }
  };

  // 配分編集ダイアログを開く
  const handleOpenEditAllocationDialog = (type: 'checkout' | 'pos', id: string) => {
    let allocation: SalesAllocation | undefined;
    if (type === 'checkout') {
      allocation = checkouts.find(c => c.id === id)?.salesAllocation;
    } else {
      allocation = posCheckouts.find(p => p.id === id)?.salesAllocation;
    }
    if (!allocation) return;
    setEditAllocTarget({ type, id });
    setEditAllocations(allocation.allocations.map(a => ({ ...a })));
    setEditAllocNetSales(allocation.netSales);
    setEditAllocationDialogOpen(true);
  };

  // 配分のresolved金額を再計算
  const recalcAllocations = (allocs: SalesAllocation['allocations'], netSales: number): SalesAllocation['allocations'] => {
    // まず固定金額を確定
    let fixedTotal = 0;
    const result = allocs.map(a => ({ ...a }));
    result.forEach(a => {
      if (a.type === 'amount') {
        a.resolved = a.value;
        fixedTotal += a.value;
      }
    });
    // 残りをパーセント配分
    const remaining = netSales - fixedTotal;
    let percentTotal = 0;
    result.forEach(a => {
      if (a.type === 'percent') {
        percentTotal += a.value;
      }
    });
    result.forEach(a => {
      if (a.type === 'percent') {
        a.resolved = percentTotal > 0 ? Math.round(remaining * a.value / percentTotal) : 0;
      }
    });
    return result;
  };

  // 配分保存処理
  const handleSaveAllocation = async () => {
    if (!editAllocTarget || !user) return;

    // 合計チェック
    const totalResolved = editAllocations.reduce((sum, a) => sum + a.resolved, 0);
    if (Math.abs(totalResolved - editAllocNetSales) > 1) {
      alert(`配分合計（${totalResolved.toLocaleString()}円）が純売上（${editAllocNetSales.toLocaleString()}円）と一致しません`);
      return;
    }

    setIsSavingAllocation(true);
    try {
      const collectionName = editAllocTarget.type === 'checkout' ? 'checkouts' : 'posCheckouts';
      const docRef = doc(db, collectionName, editAllocTarget.id);

      // 既存のsalesAllocationを取得して更新
      const updatedAllocations = editAllocations.map(a => ({
        staffId: a.staffId,
        staffName: a.staffName,
        type: a.type,
        value: a.value,
        resolved: a.resolved,
      }));

      await updateDoc(docRef, {
        'salesAllocation.allocations': updatedAllocations,
        // 後方互換: salesStaff も更新
        salesStaff: updatedAllocations.map(a => ({
          staffId: a.staffId,
          staffName: a.staffName,
          shareRate: a.resolved / editAllocNetSales,
        })),
        updatedAt: Timestamp.now(),
      });

      // ローカル状態を更新
      if (editAllocTarget.type === 'checkout') {
        setCheckouts(prev => prev.map(c =>
          c.id === editAllocTarget.id && c.salesAllocation
            ? { ...c, salesAllocation: { ...c.salesAllocation, allocations: updatedAllocations } }
            : c
        ));
      } else {
        setPosCheckouts(prev => prev.map(p =>
          p.id === editAllocTarget.id && p.salesAllocation
            ? { ...p, salesAllocation: { ...p.salesAllocation, allocations: updatedAllocations } }
            : p
        ));
      }
      setEditAllocationDialogOpen(false);
      setEditAllocTarget(null);
    } catch (error) {
      console.error('Error saving allocation:', error);
      alert('保存に失敗しました: ' + (error as Error).message);
    } finally {
      setIsSavingAllocation(false);
    }
  };

  // 商品バック確定ダイアログを開く
  const handleOpenItemBackDialog = (order: OrderData, itemIndex: number) => {
    const item = order.items[itemIndex];
    setEditingItemOrder(order);
    setEditingItemIndex(itemIndex);
    setEditingItemBackInfo({
      staffId: item.backInfo?.staffId || '',
      staffName: item.backInfo?.staffName || '',
      backRate: item.backInfo?.backRate ?? 50,
    });
    setEditItemBackDialogOpen(true);
  };

  // 商品バック確定を保存
  const handleSaveItemBack = async () => {
    if (!editingItemOrder || editingItemIndex < 0 || !user) return;

    try {
      const item = editingItemOrder.items[editingItemIndex];
      const itemSubtotal = item.subtotal || (item.price || 0) * item.quantity;
      const backAmount = Math.round(itemSubtotal * editingItemBackInfo.backRate / 100);

      const updatedItems = [...editingItemOrder.items];
      updatedItems[editingItemIndex] = {
        ...item,
        backInfo: {
          staffId: editingItemBackInfo.staffId,
          staffName: editingItemBackInfo.staffName,
          backRate: editingItemBackInfo.backRate,
          backAmount,
          confirmedAt: new Date(),
          confirmedBy: user.uid,
        },
      };

      const orderRef = doc(db, 'orders', editingItemOrder.id);
      await import('firebase/firestore').then(({ updateDoc, Timestamp }) =>
        updateDoc(orderRef, {
          items: updatedItems,
          updatedAt: Timestamp.now(),
        })
      );

      // ローカル状態を更新
      setOrders(prev => prev.map(o =>
        o.id === editingItemOrder.id
          ? { ...o, items: updatedItems }
          : o
      ));

      setEditItemBackDialogOpen(false);
      setEditingItemOrder(null);
      setEditingItemIndex(-1);
    } catch (error) {
      console.error('Error saving item back:', error);
      alert('保存に失敗しました');
    }
  };

  // 商品削除ダイアログを開く（通常クリック）
  const handleOpenDeleteItemDialog = (order: OrderData, itemIndex: number) => {
    setDeleteItemOrder(order);
    setDeleteItemIndex(itemIndex);
    setDeleteItemReason('');
    setIsHardDelete(false);
    setDeleteItemDialogOpen(true);
  };

  // 長押し開始（3秒で完全削除、それ以前で通常削除）
  const handleDeleteLongPressStart = (order: OrderData, itemIndex: number, _fromUI: boolean) => {
    setIsLongPressing(false);
    setDeleteItemOrder(order);
    setDeleteItemIndex(itemIndex);
    const timer = setTimeout(() => {
      setIsLongPressing(true);
      setDeleteItemReason('');
      setIsHardDelete(true);
      setDeleteItemDialogOpen(true);
      setLongPressTimer(null);
    }, 3000);
    setLongPressTimer(timer);
  };

  // 長押し終了
  const handleDeleteLongPressEnd = () => {
    if (longPressTimer) {
      clearTimeout(longPressTimer);
      setLongPressTimer(null);
      // 3秒以内にリリースした場合は通常削除ダイアログを表示
      if (!isLongPressing && deleteItemOrder && deleteItemIndex >= 0) {
        setDeleteItemReason('');
        setIsHardDelete(false);
        setDeleteItemDialogOpen(true);
      }
    }
  };

  // 商品を削除（履歴あり or 完全削除）
  const handleDeleteItem = async () => {
    if (!deleteItemOrder || deleteItemIndex < 0 || !user) return;

    try {
      const item = deleteItemOrder.items[deleteItemIndex];
      const itemSubtotal = item.subtotal || (item.price || 0) * item.quantity;

      if (isHardDelete) {
        // 完全削除（商品をitemsから除去）
        const updatedItems = deleteItemOrder.items.filter((_, idx) => idx !== deleteItemIndex);
        const newTotal = updatedItems.reduce((sum, i) => sum + (i.subtotal || (i.price || 0) * i.quantity), 0);

        const orderRef = doc(db, 'orders', deleteItemOrder.id);
        await import('firebase/firestore').then(({ updateDoc, Timestamp }) =>
          updateDoc(orderRef, {
            items: updatedItems,
            total: newTotal,
            updatedAt: Timestamp.now(),
          })
        );

        // ローカル状態を更新
        setOrders(prev => prev.map(o =>
          o.id === deleteItemOrder.id
            ? { ...o, items: updatedItems, total: newTotal }
            : o
        ));
      } else {
        // 履歴あり削除（cancelInfoを設定）
        if (!deleteItemReason.trim()) {
          alert('削除理由を入力してください');
          return;
        }

        const updatedItems = [...deleteItemOrder.items];
        updatedItems[deleteItemIndex] = {
          ...item,
          cancelInfo: {
            cancelledAt: new Date(),
            cancelledBy: user.uid,
            reason: deleteItemReason,
            isHardDelete: false,
          },
        };

        // 合計からキャンセルされた商品を除外
        const newTotal = updatedItems
          .filter(i => !i.cancelInfo)
          .reduce((sum, i) => sum + (i.subtotal || (i.price || 0) * i.quantity), 0);

        const orderRef = doc(db, 'orders', deleteItemOrder.id);
        await import('firebase/firestore').then(({ updateDoc, Timestamp }) =>
          updateDoc(orderRef, {
            items: updatedItems,
            total: newTotal,
            updatedAt: Timestamp.now(),
          })
        );

        // ローカル状態を更新
        setOrders(prev => prev.map(o =>
          o.id === deleteItemOrder.id
            ? { ...o, items: updatedItems, total: newTotal }
            : o
        ));
      }

      setDeleteItemDialogOpen(false);
      setDeleteItemOrder(null);
      setDeleteItemIndex(-1);
      setDeleteItemReason('');
      setIsHardDelete(false);
    } catch (error) {
      console.error('Error deleting item:', error);
      alert('削除に失敗しました');
    }
  };

  if (!shop) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography>店舗情報を読み込んでいます...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h4">売上・会計分析</Typography>

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Date Range Type */}
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>期間</InputLabel>
            <Select
              value={dateRangeType}
              label="期間"
              onChange={(e) => setDateRangeType(e.target.value as any)}
            >
              <MenuItem value="today">本日</MenuItem>
              <MenuItem value="week">過去1週間</MenuItem>
              <MenuItem value="month">過去1ヶ月</MenuItem>
              <MenuItem value="custom">カスタム</MenuItem>
            </Select>
          </FormControl>

          {/* Custom Date Range */}
          {dateRangeType === 'custom' && (
            <>
              <TextField
                type="date"
                size="small"
                label="開始日"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                type="date"
                size="small"
                label="終了日"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </>
          )}

          <Tooltip title="更新">
            <IconButton onClick={fetchAllData} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title="CSVエクスポート">
            <IconButton onClick={exportCSV} disabled={dailySales.length === 0}>
              <DownloadIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Summary Cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} sm={4} md={2}>
              <Card>
                <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                  <Typography color="textSecondary" variant="body2" gutterBottom>
                    総売上
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    {formatCurrency(totalRevenue)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            {totalGrossProfit > 0 && (
              <Grid item xs={6} sm={4} md={2}>
                <Card sx={{ bgcolor: '#e8f5e9' }}>
                  <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                    <Typography color="textSecondary" variant="body2" gutterBottom>
                      粗利益
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                      {formatCurrency(totalGrossProfit)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      ({totalRevenue > 0 ? ((totalGrossProfit / totalRevenue) * 100).toFixed(1) : 0}%)
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            )}
            {totalCommission > 0 && (
              <Grid item xs={6} sm={4} md={2}>
                <Card sx={{ bgcolor: '#fff3e0' }}>
                  <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                    <Typography color="textSecondary" variant="body2" gutterBottom>
                      コミッション
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'warning.main' }}>
                      {formatCurrency(totalCommission)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      店舗利益: {formatCurrency(totalRevenue - totalCommission)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            )}
            <Grid item xs={6} sm={4} md={2}>
              <Card>
                <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                  <Typography color="textSecondary" variant="body2" gutterBottom>
                    注文数
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    {totalOrders.toLocaleString()}件
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <Card>
                <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                  <Typography color="textSecondary" variant="body2" gutterBottom>
                    来店人数
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    {totalGuests.toLocaleString()}人
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <Card>
                <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                  <Typography color="textSecondary" variant="body2" gutterBottom>
                    平均注文単価
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    {formatCurrency(averageOrderValue)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <Card>
                <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                  <Typography color="textSecondary" variant="body2" gutterBottom>
                    客単価
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    {formatCurrency(averagePerGuest)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Tabs */}
          <Paper sx={{ mb: 3 }}>
            <Tabs
              value={tabValue}
              onChange={(_, newValue) => setTabValue(newValue)}
              variant="scrollable"
              scrollButtons="auto"
            >
              <Tab icon={<TrendingUpIcon />} label="売上推移" iconPosition="start" />
              <Tab icon={<ReceiptIcon />} label="商品分析" iconPosition="start" />
              <Tab icon={<PeopleIcon />} label="来店分析" iconPosition="start" />
              <Tab icon={<LanguageIcon />} label="言語・国別" iconPosition="start" />
              <Tab icon={<RepeatIcon />} label="リピーター" iconPosition="start" />
              <Tab icon={<CancelIcon />} label="キャンセル" iconPosition="start" />
              <Tab icon={<PaymentIcon />} label="会計履歴" iconPosition="start" />
              <Tab icon={<PersonIcon />} label="スタッフ分析" iconPosition="start" />
            </Tabs>
          </Paper>

          {/* Tab 0: Sales Trends */}
          <TabPanel value={tabValue} index={0}>
            {/* Daily Sales Chart */}
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                日別売上推移
              </Typography>
              <ResponsiveContainer width="100%" height={350}>
                <AreaChart data={dailySales}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="displayDate" />
                  <YAxis yAxisId="left" tickFormatter={(v) => `${formatCurrency(v)}`} />
                  <YAxis yAxisId="right" orientation="right" />
                  <RechartsTooltip
                    formatter={(value: number, name: string) => {
                      if (name === '売上') return formatCurrency(value);
                      return value;
                    }}
                  />
                  <Legend />
                  <Area
                    yAxisId="left"
                    type="monotone"
                    dataKey="revenue"
                    name="売上"
                    stroke="#8884d8"
                    fill="#8884d8"
                    fillOpacity={0.3}
                  />
                  <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey="orderCount"
                    name="注文数"
                    stroke="#82ca9d"
                    strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </Paper>

            {/* Hourly Sales Chart */}
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                時間帯別売上
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={hourlySales}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="hour" />
                  <YAxis tickFormatter={(v) => formatCurrency(v)} />
                  <RechartsTooltip formatter={(value: number) => formatCurrency(value)} />
                  <Bar dataKey="revenue" name="売上" fill="#8884d8" />
                </BarChart>
              </ResponsiveContainer>
            </Paper>

            {/* Daily Details Table */}
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                日別詳細（日計）
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                日付をクリックすると伝票一覧を表示します
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>日付</TableCell>
                      <TableCell align="right">売上</TableCell>
                      <TableCell align="right">注文数</TableCell>
                      <TableCell align="right">来店人数</TableCell>
                      <TableCell align="right">客単価</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {dailySales.slice().reverse().map((day) => (
                      <TableRow
                        key={day.date}
                        hover
                        sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' } }}
                        onClick={() => handleDateClick(day.date)}
                      >
                        <TableCell sx={{ color: 'primary.main', fontWeight: 'medium' }}>
                          {format(parseISO(day.date), 'yyyy/MM/dd (E)', { locale: ja })}
                        </TableCell>
                        <TableCell align="right">{formatCurrency(day.revenue)}</TableCell>
                        <TableCell align="right">{day.orderCount}件</TableCell>
                        <TableCell align="right">{day.guestCount}人</TableCell>
                        <TableCell align="right">{formatCurrency(day.averagePerGuest)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </TabPanel>

          {/* Tab 1: Product Analysis */}
          <TabPanel value={tabValue} index={1}>
            {/* Top Products Chart */}
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                売上トップ10商品
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={productSales.slice(0, 10)} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" tickFormatter={(v) => formatCurrency(v)} />
                  <YAxis type="category" dataKey="productName" width={150} />
                  <RechartsTooltip formatter={(value: number) => formatCurrency(value)} />
                  <Bar dataKey="totalRevenue" name="売上" fill="#8884d8" />
                </BarChart>
              </ResponsiveContainer>
            </Paper>

            {/* ABC Analysis */}
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                ABC分析
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                A: 累計70%まで（重要商品）/ B: 累計90%まで（中程度）/ C: 残り（低優先度）
              </Typography>

              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>ランク</TableCell>
                      <TableCell>商品名</TableCell>
                      <TableCell align="right">販売数</TableCell>
                      <TableCell align="right">売上</TableCell>
                      <TableCell align="right">構成比</TableCell>
                      <TableCell align="right">累計</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {abcProducts.map((product) => (
                      <TableRow key={product.productId}>
                        <TableCell>
                          <Chip
                            label={product.rank}
                            size="small"
                            sx={{
                              backgroundColor: getRankColor(product.rank),
                              color: 'white',
                              fontWeight: 'bold',
                            }}
                          />
                        </TableCell>
                        <TableCell>{product.productName}</TableCell>
                        <TableCell align="right">{product.totalQuantity}</TableCell>
                        <TableCell align="right">{formatCurrency(product.totalRevenue)}</TableCell>
                        <TableCell align="right">{product.revenuePercentage.toFixed(1)}%</TableCell>
                        <TableCell align="right">{product.cumulativePercentage.toFixed(1)}%</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </TabPanel>

          {/* Tab 2: Customer Analysis */}
          <TabPanel value={tabValue} index={2}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    来店人数推移
                  </Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={dailySales}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="displayDate" />
                      <YAxis />
                      <RechartsTooltip />
                      <Legend />
                      <Line type="monotone" dataKey="guestCount" name="来店人数" stroke="#82ca9d" strokeWidth={2} />
                    </LineChart>
                  </ResponsiveContainer>
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    客単価推移
                  </Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={dailySales}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="displayDate" />
                      <YAxis tickFormatter={(v) => formatCurrency(v)} />
                      <RechartsTooltip formatter={(value: number) => formatCurrency(value)} />
                      <Legend />
                      <Line type="monotone" dataKey="averagePerGuest" name="客単価" stroke="#ff7300" strokeWidth={2} />
                    </LineChart>
                  </ResponsiveContainer>
                </Paper>
              </Grid>
            </Grid>
          </TabPanel>

          {/* Tab 3: Language/Country Analysis */}
          <TabPanel value={tabValue} index={3}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    言語別利用割合
                  </Typography>
                  {languageStats.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={languageStats}
                          dataKey="orderCount"
                          nameKey="displayName"
                          cx="50%"
                          cy="50%"
                          outerRadius={100}
                          label={({ name, percent }) => `${name} (${((percent || 0) * 100).toFixed(1)}%)`}
                        >
                          {languageStats.map((entry, index) => (
                            <Cell key={entry.language} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <RechartsTooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <Box sx={{ py: 8, textAlign: 'center' }}>
                      <Typography color="textSecondary">データがありません</Typography>
                    </Box>
                  )}
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    言語別売上
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>言語</TableCell>
                          <TableCell align="right">注文数</TableCell>
                          <TableCell align="right">売上</TableCell>
                          <TableCell align="right">割合</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {languageStats.map((stat) => (
                          <TableRow key={stat.language}>
                            <TableCell>{stat.displayName}</TableCell>
                            <TableCell align="right">{stat.orderCount}件</TableCell>
                            <TableCell align="right">{formatCurrency(stat.revenue)}</TableCell>
                            <TableCell align="right">{stat.percentage.toFixed(1)}%</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              </Grid>
            </Grid>
          </TabPanel>

          {/* Tab 4: Repeat Customer Analysis */}
          <TabPanel value={tabValue} index={4}>
            {repeatStats ? (
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <Card>
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Typography color="textSecondary" gutterBottom>
                        リピーター率
                      </Typography>
                      <Typography variant="h3" sx={{ color: '#4caf50', fontWeight: 'bold' }}>
                        {repeatStats.repeatRate.toFixed(1)}%
                      </Typography>
                      <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                        {repeatStats.repeatCustomers}人 / {repeatStats.newCustomers + repeatStats.repeatCustomers}人
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Card>
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Typography color="textSecondary" gutterBottom>
                        新規顧客
                      </Typography>
                      <Typography variant="h3" sx={{ fontWeight: 'bold' }}>
                        {repeatStats.newCustomers}人
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Card>
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Typography color="textSecondary" gutterBottom>
                        リピーター
                      </Typography>
                      <Typography variant="h3" sx={{ fontWeight: 'bold', color: '#2196f3' }}>
                        {repeatStats.repeatCustomers}人
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12}>
                  <Paper sx={{ p: 3 }}>
                    <Typography variant="h6" gutterBottom>
                      来店頻度分布
                    </Typography>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={repeatStats.visitFrequency}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="visits" />
                        <YAxis />
                        <RechartsTooltip />
                        <Bar dataKey="count" name="顧客数" fill="#8884d8" />
                      </BarChart>
                    </ResponsiveContainer>
                  </Paper>
                </Grid>
              </Grid>
            ) : (
              <Paper sx={{ p: 3, textAlign: 'center' }}>
                <Typography color="textSecondary">
                  顧客データがありません。モバイルオーダーを利用した顧客のリピート情報がここに表示されます。
                </Typography>
              </Paper>
            )}
          </TabPanel>

          {/* Tab 5: Cancelled Orders */}
          <TabPanel value={tabValue} index={5}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                キャンセル済み注文一覧
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                スタッフがキャンセルした注文が表示されます。確認後、削除することができます。
              </Typography>

              {orders.filter(o => o.status === 'cancelled').length === 0 ? (
                <Box sx={{ py: 4, textAlign: 'center' }}>
                  <Typography color="textSecondary">
                    キャンセルされた注文はありません
                  </Typography>
                </Box>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell width={50}></TableCell>
                        <TableCell>日時</TableCell>
                        <TableCell>テーブル</TableCell>
                        <TableCell>キャンセル担当</TableCell>
                        <TableCell>理由</TableCell>
                        <TableCell align="right">金額</TableCell>
                        {canDeleteOrders && <TableCell width={80} align="center">操作</TableCell>}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {orders
                        .filter(o => o.status === 'cancelled')
                        .sort((a, b) => b.orderedAt.getTime() - a.orderedAt.getTime())
                        .map((order) => (
                          <React.Fragment key={order.id}>
                            <TableRow
                              hover
                              sx={{ cursor: 'pointer' }}
                              onClick={() => setExpandedOrderId(expandedOrderId === order.id ? null : order.id)}
                            >
                              <TableCell>
                                <IconButton size="small">
                                  {expandedOrderId === order.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                </IconButton>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">
                                  {format(order.orderedAt, 'yyyy/MM/dd HH:mm', { locale: ja })}
                                </Typography>
                                {order.cancelInfo?.cancelledAt && (
                                  <Typography variant="caption" color="error">
                                    キャンセル: {format(order.cancelInfo.cancelledAt, 'HH:mm')}
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell>{order.tableName || order.tableId || '-'}</TableCell>
                              <TableCell>
                                {order.cancelInfo?.staffName || '不明'}
                              </TableCell>
                              <TableCell>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    maxWidth: 200,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {order.cancelInfo?.reason || '理由未入力'}
                                </Typography>
                              </TableCell>
                              <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                                {formatCurrency(order.total)}
                              </TableCell>
                              {canDeleteOrders && (
                              <TableCell align="center">
                                <Tooltip title="この注文を削除">
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={(e) => handleOpenDeleteDialog(order, e)}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                              )}
                            </TableRow>
                            <TableRow>
                              <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
                                <Collapse in={expandedOrderId === order.id} timeout="auto" unmountOnExit>
                                  <Box sx={{ py: 2, px: 2, backgroundColor: 'grey.50' }}>
                                    {/* キャンセル情報 */}
                                    <Box sx={{ mb: 2, p: 2, backgroundColor: 'error.50', borderRadius: 1, border: '1px solid', borderColor: 'error.200' }}>
                                      <Typography variant="subtitle2" color="error.main" gutterBottom>
                                        キャンセル情報
                                      </Typography>
                                      <Grid container spacing={2}>
                                        <Grid item xs={4}>
                                          <Typography variant="body2" color="textSecondary">担当スタッフ</Typography>
                                          <Typography>{order.cancelInfo?.staffName || '不明'}</Typography>
                                        </Grid>
                                        <Grid item xs={4}>
                                          <Typography variant="body2" color="textSecondary">キャンセル日時</Typography>
                                          <Typography>
                                            {order.cancelInfo?.cancelledAt
                                              ? format(order.cancelInfo.cancelledAt, 'yyyy/MM/dd HH:mm')
                                              : '-'}
                                          </Typography>
                                        </Grid>
                                        <Grid item xs={4}>
                                          <Typography variant="body2" color="textSecondary">理由</Typography>
                                          <Typography>{order.cancelInfo?.reason || '理由未入力'}</Typography>
                                        </Grid>
                                      </Grid>
                                    </Box>

                                    {/* 注文内容 */}
                                    <Typography variant="subtitle2" gutterBottom>
                                      注文内容
                                    </Typography>
                                    <Table size="small" sx={{ backgroundColor: 'white' }}>
                                      <TableHead>
                                        <TableRow>
                                          <TableCell>商品名</TableCell>
                                          <TableCell align="right">数量</TableCell>
                                          <TableCell align="right">単価</TableCell>
                                          <TableCell align="right">小計</TableCell>
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        {order.items.map((item, idx) => (
                                          <TableRow key={idx}>
                                            <TableCell>{item.productName}</TableCell>
                                            <TableCell align="right">{item.quantity}</TableCell>
                                            <TableCell align="right">
                                              {item.price ? formatCurrency(item.price) : '-'}
                                            </TableCell>
                                            <TableCell align="right">
                                              {item.subtotal ? formatCurrency(item.subtotal) : '-'}
                                            </TableCell>
                                          </TableRow>
                                        ))}
                                      </TableBody>
                                    </Table>
                                    <Divider sx={{ my: 1 }} />
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                                      <Typography variant="body2" color="textSecondary">
                                        注文ID: {order.id.slice(0, 8)}...
                                      </Typography>
                                      <Typography variant="body2" fontWeight="bold">
                                        合計: {formatCurrency(order.total)}
                                      </Typography>
                                    </Box>
                                  </Box>
                                </Collapse>
                              </TableCell>
                            </TableRow>
                          </React.Fragment>
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {/* キャンセル統計 */}
              {orders.filter(o => o.status === 'cancelled').length > 0 && (
                <Box sx={{ mt: 3, p: 2, backgroundColor: 'grey.100', borderRadius: 1 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    キャンセル統計（選択期間）
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={4}>
                      <Typography variant="body2" color="textSecondary">キャンセル件数</Typography>
                      <Typography variant="h6">
                        {orders.filter(o => o.status === 'cancelled').length}件
                      </Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="body2" color="textSecondary">キャンセル金額合計</Typography>
                      <Typography variant="h6">
                        {formatCurrency(orders.filter(o => o.status === 'cancelled').reduce((sum, o) => sum + o.total, 0))}
                      </Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="body2" color="textSecondary">キャンセル率</Typography>
                      <Typography variant="h6">
                        {orders.length > 0
                          ? ((orders.filter(o => o.status === 'cancelled').length / orders.length) * 100).toFixed(1)
                          : 0}%
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              )}
            </Paper>
          </TabPanel>

          {/* Tab 6: 会計履歴 */}
          <TabPanel value={tabValue} index={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                会計履歴（担当者・支払い方法別）
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                期間内の会計データを担当者・支払い方法とともに表示します
              </Typography>

              {checkouts.filter(c => c.status === 'completed').length === 0 ? (
                <Box sx={{ py: 4, textAlign: 'center' }}>
                  <Typography color="textSecondary">
                    この期間の会計データはありません
                  </Typography>
                </Box>
              ) : (
                <>
                  {/* 会計履歴サマリー */}
                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={3}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography variant="body2" color="textSecondary">会計件数</Typography>
                          <Typography variant="h5">
                            {checkouts.filter(c => c.status === 'completed').length}件
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography variant="body2" color="textSecondary">合計金額</Typography>
                          <Typography variant="h5">
                            {formatCurrency(checkouts.filter(c => c.status === 'completed').reduce((sum, c) => sum + c.total, 0))}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography variant="body2" color="textSecondary">現金決済</Typography>
                          <Typography variant="h5">
                            {checkouts.filter(c => c.status === 'completed' && c.paymentMethod === 'cash').length}件
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography variant="body2" color="textSecondary">その他決済</Typography>
                          <Typography variant="h5">
                            {checkouts.filter(c => c.status === 'completed' && c.paymentMethod !== 'cash').length}件
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>

                  {/* 顧客要望(D): 支払方法フィルタ */}
                  <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      支払方法フィルタ:
                    </Typography>
                    <FormControl size="small" sx={{ minWidth: 200 }}>
                      <Select
                        value={paymentMethodFilter}
                        onChange={(e) => setPaymentMethodFilter(e.target.value as string)}
                      >
                        <MenuItem value="all">すべて</MenuItem>
                        <MenuItem value="cash">現金のみ</MenuItem>
                        <MenuItem value="non_cash">現金以外（カード/QR等）</MenuItem>
                        {(shop?.paymentMethods || []).filter(m => m.isActive).map((m) => (
                          <MenuItem key={m.code} value={m.code}>{m.name}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    {paymentMethodFilter !== 'all' && (
                      <Chip
                        label="フィルタ解除"
                        size="small"
                        onDelete={() => setPaymentMethodFilter('all')}
                        onClick={() => setPaymentMethodFilter('all')}
                      />
                    )}
                  </Box>

                  {/* 会計履歴テーブル */}
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>日時</TableCell>
                          <TableCell>テーブル</TableCell>
                          <TableCell align="center">人数</TableCell>
                          <TableCell>支払い方法</TableCell>
                          <TableCell>担当者</TableCell>
                          <TableCell align="right">小計</TableCell>
                          <TableCell align="right">税</TableCell>
                          <TableCell align="right">合計</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {checkouts
                          .filter(c => c.status === 'completed')
                          .filter(c => {
                            if (paymentMethodFilter === 'all') return true;
                            if (paymentMethodFilter === 'cash') return c.paymentMethod === 'cash';
                            if (paymentMethodFilter === 'non_cash') return c.paymentMethod !== 'cash';
                            return c.paymentMethod === paymentMethodFilter;
                          })
                          .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime())
                          .map((checkout) => (
                            <TableRow key={checkout.id} hover>
                              <TableCell>
                                {format(checkout.createdAt, 'MM/dd HH:mm')}
                              </TableCell>
                              <TableCell>
                                {checkout.tableName || checkout.tableId || '-'}
                              </TableCell>
                              <TableCell align="center">
                                {checkout.guestCount || '-'}
                              </TableCell>
                              <TableCell>
                                <Chip
                                  size="small"
                                  label={checkout.paymentMethodName || getPaymentMethodLabel(checkout.paymentMethod)}
                                  color={checkout.paymentMethod === 'cash' ? 'success' : 'primary'}
                                  variant={checkout.paymentMethod === 'cash' ? 'filled' : 'outlined'}
                                />
                              </TableCell>
                              <TableCell>
                                {checkout.paidBy ? (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <PersonIcon fontSize="small" color="action" />
                                    <Typography variant="body2">
                                      {checkout.paidBy.staffName}
                                    </Typography>
                                  </Box>
                                ) : (
                                  <Typography variant="body2" color="textSecondary">-</Typography>
                                )}
                              </TableCell>
                              <TableCell align="right">
                                {formatCurrency(checkout.subtotal)}
                              </TableCell>
                              <TableCell align="right">
                                {formatCurrency(checkout.tax)}
                              </TableCell>
                              <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                                {formatCurrency(checkout.total)}
                              </TableCell>
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {/* 担当者別集計 */}
                  <Divider sx={{ my: 3 }} />
                  <Typography variant="h6" gutterBottom>
                    担当者別集計
                  </Typography>
                  <Grid container spacing={2}>
                    {(() => {
                      const staffStats = checkouts
                        .filter(c => c.status === 'completed' && c.paidBy)
                        .reduce((acc, c) => {
                          const staffId = c.paidBy!.staffId;
                          if (!acc[staffId]) {
                            acc[staffId] = {
                              staffName: c.paidBy!.staffName,
                              count: 0,
                              total: 0,
                            };
                          }
                          acc[staffId].count++;
                          acc[staffId].total += c.total;
                          return acc;
                        }, {} as Record<string, { staffName: string; count: number; total: number }>);

                      return Object.entries(staffStats).map(([staffId, stats]) => (
                        <Grid item xs={12} sm={6} md={4} key={staffId}>
                          <Card variant="outlined">
                            <CardContent>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <PersonIcon color="primary" />
                                <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                                  {stats.staffName}
                                </Typography>
                              </Box>
                              <Grid container spacing={1}>
                                <Grid item xs={6}>
                                  <Typography variant="body2" color="textSecondary">会計件数</Typography>
                                  <Typography variant="h6">{stats.count}件</Typography>
                                </Grid>
                                <Grid item xs={6}>
                                  <Typography variant="body2" color="textSecondary">合計金額</Typography>
                                  <Typography variant="h6">{formatCurrency(stats.total)}</Typography>
                                </Grid>
                              </Grid>
                            </CardContent>
                          </Card>
                        </Grid>
                      ));
                    })()}
                    {checkouts.filter(c => c.status === 'completed' && c.paidBy).length === 0 && (
                      <Grid item xs={12}>
                        <Typography color="textSecondary" sx={{ textAlign: 'center', py: 2 }}>
                          担当者情報がある会計データがありません
                        </Typography>
                      </Grid>
                    )}
                  </Grid>

                  {/* 支払い方法別集計 */}
                  <Divider sx={{ my: 3 }} />
                  <Typography variant="h6" gutterBottom>
                    支払い方法別集計
                  </Typography>
                  <Grid container spacing={2}>
                    {(() => {
                      const paymentStats = checkouts
                        .filter(c => c.status === 'completed')
                        .reduce((acc, c) => {
                          const method = c.paymentMethodName || c.paymentMethod;
                          if (!acc[method]) {
                            acc[method] = {
                              label: c.paymentMethodName || getPaymentMethodLabel(c.paymentMethod),
                              count: 0,
                              total: 0,
                            };
                          }
                          acc[method].count++;
                          acc[method].total += c.total;
                          return acc;
                        }, {} as Record<string, { label: string; count: number; total: number }>);

                      return Object.entries(paymentStats).map(([method, stats]) => (
                        <Grid item xs={12} sm={6} md={4} key={method}>
                          <Card variant="outlined">
                            <CardContent>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <PaymentIcon color="primary" />
                                <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                                  {stats.label}
                                </Typography>
                              </Box>
                              <Grid container spacing={1}>
                                <Grid item xs={6}>
                                  <Typography variant="body2" color="textSecondary">件数</Typography>
                                  <Typography variant="h6">{stats.count}件</Typography>
                                </Grid>
                                <Grid item xs={6}>
                                  <Typography variant="body2" color="textSecondary">金額</Typography>
                                  <Typography variant="h6">{formatCurrency(stats.total)}</Typography>
                                </Grid>
                              </Grid>
                            </CardContent>
                          </Card>
                        </Grid>
                      ));
                    })()}
                  </Grid>
                </>
              )}
            </Paper>
          </TabPanel>

          {/* Tab 7: Staff Analysis */}
          <TabPanel value={tabValue} index={7}>
            <Grid container spacing={3}>
              {/* Bar Chart - Staff Sales Trends */}
              <Grid item xs={12} md={8}>
                <Paper sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">スタッフ別売上推移</Typography>
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                      <InputLabel>スタッフ</InputLabel>
                      <Select
                        value={selectedStaffId}
                        label="スタッフ"
                        onChange={(e) => setSelectedStaffId(e.target.value)}
                      >
                        <MenuItem value="all">全員</MenuItem>
                        {staffSalesData.map(staff => (
                          <MenuItem key={staff.staffId} value={staff.staffId}>{staff.staffName}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                  {staffDailySales.length > 0 ? (
                    <ResponsiveContainer width="100%" height={350}>
                      <BarChart data={staffDailySales}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="displayDate" />
                        <YAxis tickFormatter={(v) => formatCurrency(v)} />
                        <RechartsTooltip
                          formatter={(value: number, name: string) => {
                            const staff = staffSalesData.find(s => s.staffId === name);
                            return [formatCurrency(value), staff?.staffName || name];
                          }}
                        />
                        <Legend
                          formatter={(value: string) => {
                            const staff = staffSalesData.find(s => s.staffId === value);
                            return staff?.staffName || value;
                          }}
                        />
                        {selectedStaffId === 'all'
                          ? staffSalesData.map((staff, i) => (
                              <Bar
                                key={staff.staffId}
                                dataKey={staff.staffId}
                                stackId="a"
                                fill={COLORS[i % COLORS.length]}
                              />
                            ))
                          : <Bar dataKey={selectedStaffId} fill={COLORS[0]} />
                        }
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <Typography color="text.secondary" textAlign="center" py={8}>
                      スタッフ売上データがありません
                    </Typography>
                  )}
                </Paper>
              </Grid>

              {/* Pie Chart - Sales Composition */}
              <Grid item xs={12} md={4}>
                <Paper sx={{ p: 3, height: '100%' }}>
                  <Typography variant="h6" gutterBottom>売上構成比</Typography>
                  {staffSalesData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={350}>
                      <PieChart>
                        <Pie
                          data={staffSalesData.map(s => ({ name: s.staffName, value: s.totalSales }))}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, percent }: { name?: string; percent?: number }) => `${name || ''} ${((percent || 0) * 100).toFixed(0)}%`}
                          outerRadius={100}
                          dataKey="value"
                        >
                          {staffSalesData.map((_, i) => (
                            <Cell key={`cell-${i}`} fill={COLORS[i % COLORS.length]} />
                          ))}
                        </Pie>
                        <RechartsTooltip
                          formatter={(value: number) => formatCurrency(value)}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <Typography color="text.secondary" textAlign="center" py={8}>
                      データなし
                    </Typography>
                  )}
                </Paper>
              </Grid>

              {/* Ranking Table */}
              <Grid item xs={12}>
                <Paper sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom>スタッフ別売上ランキング</Typography>
                  {staffSalesData.length > 0 ? (
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>#</TableCell>
                            <TableCell>スタッフ名</TableCell>
                            <TableCell align="right">売上合計</TableCell>
                            <TableCell align="right">件数</TableCell>
                            <TableCell align="right">客単価</TableCell>
                            <TableCell align="right">構成比</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {(() => {
                            const grandTotal = staffSalesData.reduce((sum, s) => sum + s.totalSales, 0);
                            return staffSalesData.map((staff, index) => (
                              <TableRow key={staff.staffId}>
                                <TableCell>{index + 1}</TableCell>
                                <TableCell>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Avatar sx={{ width: 28, height: 28, bgcolor: COLORS[index % COLORS.length], fontSize: '0.8rem' }}>
                                      {staff.staffName.charAt(0)}
                                    </Avatar>
                                    {staff.staffName}
                                  </Box>
                                </TableCell>
                                <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                                  {formatCurrency(staff.totalSales)}
                                </TableCell>
                                <TableCell align="right">{staff.orderCount}件</TableCell>
                                <TableCell align="right">
                                  {staff.orderCount > 0 ? formatCurrency(staff.totalSales / staff.orderCount) : '-'}
                                </TableCell>
                                <TableCell align="right">
                                  {grandTotal > 0 ? `${((staff.totalSales / grandTotal) * 100).toFixed(1)}%` : '-'}
                                </TableCell>
                              </TableRow>
                            ));
                          })()}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Typography color="text.secondary" textAlign="center" py={4}>
                      期間内のスタッフ売上データがありません
                    </Typography>
                  )}
                </Paper>
              </Grid>
            </Grid>
          </TabPanel>
        </>
      )}

      {/* 伝票一覧ダイアログ */}
      <Dialog
        open={dailyOrdersDialogOpen}
        onClose={() => setDailyOrdersDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6">
              {selectedDate && format(parseISO(selectedDate), 'yyyy年MM月dd日 (E)', { locale: ja })} の会計一覧
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {selectedDate && (() => {
                const checkoutCount = getCheckoutsForDate(selectedDate).length;
                const orderCount = getOrdersForDate(selectedDate).length;
                const posCount = getPosCheckoutsForDate(selectedDate).length;
                const parts: string[] = [];
                if (checkoutCount > 0) parts.push(`会計: ${checkoutCount}件`);
                if (posCount > 0) parts.push(`POS: ${posCount}件`);
                if (orderCount > 0) parts.push(`注文: ${orderCount}件`);
                return parts.length > 0 ? parts.join(' / ') : 'データなし';
              })()}
            </Typography>
          </Box>
          <IconButton onClick={() => setDailyOrdersDialogOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedDate && (
            <>
              {/* 会計データがある場合: 会計→注文の階層表示 */}
              {getCheckoutsForDate(selectedDate).length > 0 ? (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell width={50}></TableCell>
                        <TableCell>会計時刻</TableCell>
                        <TableCell>テーブル</TableCell>
                        <TableCell align="center">人数</TableCell>
                        <TableCell>支払方法</TableCell>
                        <TableCell align="right">合計金額</TableCell>
                        {canDeleteOrders && <TableCell width={60} align="center">削除</TableCell>}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {getCheckoutsForDate(selectedDate).map((checkout) => {
                        const checkoutOrders = getOrdersForCheckout(checkout);
                        return (
                          <React.Fragment key={checkout.id}>
                            <TableRow
                              hover
                              sx={{
                                cursor: 'pointer',
                                backgroundColor: expandedCheckoutId === checkout.id ? 'primary.50' : 'inherit',
                                opacity: checkout.salesStatus === 'confirmed' ? 0.6 : 1,
                              }}
                              onClick={() => setExpandedCheckoutId(expandedCheckoutId === checkout.id ? null : checkout.id)}
                            >
                              <TableCell>
                                <IconButton size="small">
                                  {expandedCheckoutId === checkout.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                </IconButton>
                              </TableCell>
                              <TableCell>
                                {format(checkout.createdAt, 'HH:mm')}
                                {checkout.salesStatus === 'confirmed' && (
                                  <Chip label="確定" size="small" color="default" sx={{ ml: 0.5, height: 18, fontSize: '0.65rem' }} />
                                )}
                                {checkout.salesStatus === 'pending' && (
                                  <Chip label="未確定" size="small" color="warning" sx={{ ml: 0.5, height: 18, fontSize: '0.65rem' }} />
                                )}
                              </TableCell>
                              <TableCell>{checkout.tableName || checkout.tableId || '-'}</TableCell>
                              <TableCell align="center">{checkout.guestCount || '-'}名</TableCell>
                              <TableCell>{getPaymentMethodLabel(checkout.paymentMethod)}</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                                {formatCurrency(checkout.total)}
                              </TableCell>
                              {canDeleteOrders && (
                              <TableCell align="center">
                                {checkout.salesStatus === 'pending' && (
                                  <Tooltip title="売上確定">
                                    <IconButton
                                      size="small"
                                      color="success"
                                      onClick={(e) => { e.stopPropagation(); handleOpenConfirmDialog('checkout', checkout.id); }}
                                    >
                                      <CheckCircleOutlineIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                {checkout.salesAllocation && checkout.salesStatus !== 'confirmed' && (
                                  <Tooltip title="配分編集">
                                    <IconButton
                                      size="small"
                                      color="primary"
                                      onClick={(e) => { e.stopPropagation(); handleOpenEditAllocationDialog('checkout', checkout.id); }}
                                    >
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                <Tooltip title="この会計を削除">
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={(e) => handleOpenDeleteCheckoutDialog(checkout, e)}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                              )}
                            </TableRow>
                            <TableRow>
                              <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
                                <Collapse in={expandedCheckoutId === checkout.id} timeout="auto" unmountOnExit>
                                  <Box sx={{ py: 2, px: 2, backgroundColor: 'grey.50' }}>
                                    {/* 会計内訳 */}
                                    <Box sx={{ mb: 2, p: 1, backgroundColor: 'white', borderRadius: 1 }}>
                                      <Typography variant="subtitle2" gutterBottom>会計内訳</Typography>
                                      <Grid container spacing={1}>
                                        <Grid item xs={4}>
                                          <Typography variant="body2" color="textSecondary">小計</Typography>
                                          <Typography>{formatCurrency(checkout.subtotal)}</Typography>
                                        </Grid>
                                        <Grid item xs={4}>
                                          <Typography variant="body2" color="textSecondary">税</Typography>
                                          <Typography>{formatCurrency(checkout.tax)}</Typography>
                                        </Grid>
                                        {checkout.serviceCharge > 0 && (
                                          <Grid item xs={4}>
                                            <Typography variant="body2" color="textSecondary">サービス料</Typography>
                                            <Typography>{formatCurrency(checkout.serviceCharge)}</Typography>
                                          </Grid>
                                        )}
                                      </Grid>
                                    </Box>

                                    {/* 売上配分情報 */}
                                    {checkout.salesAllocation && (
                                      <Box sx={{ mb: 2, p: 1.5, backgroundColor: checkout.salesStatus === 'confirmed' ? 'grey.200' : 'info.50', borderRadius: 1 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                          <Typography variant="subtitle2">
                                            売上配分
                                            {checkout.salesStatus === 'confirmed' && (
                                              <Chip label="確定済" size="small" color="default" sx={{ ml: 1, height: 18, fontSize: '0.65rem' }} />
                                            )}
                                          </Typography>
                                          <Typography variant="caption" color="textSecondary">
                                            純売上: {formatCurrency(checkout.salesAllocation.netSales)}
                                            （原価: {formatCurrency(checkout.salesAllocation.totalCost)}）
                                          </Typography>
                                        </Box>
                                        {checkout.salesAllocation.allocations.map((alloc, i) => (
                                          <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                                            <Typography variant="body2">
                                              {alloc.staffName}
                                              <Typography component="span" variant="caption" color="textSecondary" sx={{ ml: 1 }}>
                                                ({alloc.type === 'percent' ? `${alloc.value}%` : '固定額'})
                                              </Typography>
                                            </Typography>
                                            <Typography variant="body2" fontWeight="bold">
                                              {formatCurrency(alloc.resolved)}
                                            </Typography>
                                          </Box>
                                        ))}
                                      </Box>
                                    )}

                                    {/* この会計に含まれる注文一覧 */}
                                    <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                                      注文履歴（{checkoutOrders.length}件）
                                    </Typography>
                                    {checkoutOrders.length === 0 ? (
                                      <Typography variant="body2" color="textSecondary">
                                        注文データが見つかりません
                                      </Typography>
                                    ) : (
                                      <Table size="small" sx={{ backgroundColor: 'white' }}>
                                        <TableHead>
                                          <TableRow>
                                            <TableCell width={40}></TableCell>
                                            <TableCell>注文時刻</TableCell>
                                            <TableCell>ステータス</TableCell>
                                            <TableCell align="right">金額</TableCell>
                                            {canDeleteOrders && <TableCell width={50} align="center">削除</TableCell>}
                                          </TableRow>
                                        </TableHead>
                                        <TableBody>
                                          {checkoutOrders.map((order) => (
                                            <React.Fragment key={order.id}>
                                              <TableRow
                                                hover
                                                sx={{ cursor: 'pointer' }}
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  setExpandedOrderId(expandedOrderId === order.id ? null : order.id);
                                                }}
                                              >
                                                <TableCell>
                                                  <IconButton size="small">
                                                    {expandedOrderId === order.id ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                                                  </IconButton>
                                                </TableCell>
                                                <TableCell>{format(order.orderedAt, 'HH:mm')}</TableCell>
                                                <TableCell>
                                                  <Chip
                                                    label={getStatusLabel(order.status)}
                                                    color={getStatusColor(order.status)}
                                                    size="small"
                                                  />
                                                  {order.salesStaff && order.salesStaff.length > 0 && (
                                                    <Chip
                                                      label={`${order.salesStaff.map(s => s.staffName).join(', ')}`}
                                                      size="small"
                                                      variant="outlined"
                                                      sx={{ ml: 0.5 }}
                                                    />
                                                  )}
                                                </TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 'medium' }}>
                                                  {formatCurrency(order.total)}
                                                </TableCell>
                                                <TableCell align="center">
                                                  {shop?.costBackSettings?.enabled && order.salesStaff && order.salesStaff.length > 0 && (
                                                    <Tooltip title="バック率編集">
                                                      <IconButton
                                                        size="small"
                                                        color="primary"
                                                        onClick={(e) => handleOpenEditBackRateDialog(order, e)}
                                                      >
                                                        <EditIcon fontSize="small" />
                                                      </IconButton>
                                                    </Tooltip>
                                                  )}
                                                  {canDeleteOrders && (
                                                  <Tooltip title="この注文を削除">
                                                    <IconButton
                                                      size="small"
                                                      color="error"
                                                      onClick={(e) => handleOpenDeleteDialog(order, e)}
                                                    >
                                                      <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                  </Tooltip>
                                                  )}
                                                </TableCell>
                                              </TableRow>
                                              <TableRow>
                                                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={5}>
                                                  <Collapse in={expandedOrderId === order.id} timeout="auto" unmountOnExit>
                                                    <Box sx={{ py: 1, px: 2, backgroundColor: 'grey.100' }}>
                                                      <Typography variant="caption" color="textSecondary">注文内容</Typography>
                                                      <Table size="small">
                                                        <TableHead>
                                                          <TableRow>
                                                            <TableCell>商品</TableCell>
                                                            <TableCell align="center">数量</TableCell>
                                                            <TableCell align="right">小計</TableCell>
                                                            <TableCell align="center">バック</TableCell>
                                                            <TableCell align="center" width={80}>操作</TableCell>
                                                          </TableRow>
                                                        </TableHead>
                                                        <TableBody>
                                                          {order.items.map((item, idx) => (
                                                            <TableRow
                                                              key={idx}
                                                              sx={{
                                                                backgroundColor: item.cancelInfo ? 'error.50' : 'inherit',
                                                                opacity: item.cancelInfo ? 0.6 : 1,
                                                              }}
                                                            >
                                                              <TableCell>
                                                                <Box>
                                                                  <Typography variant="body2">
                                                                    {item.productName}
                                                                    {item.cancelInfo && (
                                                                      <Chip
                                                                        label="削除済"
                                                                        size="small"
                                                                        color="error"
                                                                        sx={{ ml: 1, height: 18 }}
                                                                      />
                                                                    )}
                                                                  </Typography>
                                                                  {item.cancelInfo && (
                                                                    <Typography variant="caption" color="error">
                                                                      理由: {item.cancelInfo.reason}
                                                                    </Typography>
                                                                  )}
                                                                </Box>
                                                              </TableCell>
                                                              <TableCell align="center">×{item.quantity}</TableCell>
                                                              <TableCell align="right">
                                                                {item.subtotal ? formatCurrency(item.subtotal) : '-'}
                                                              </TableCell>
                                                              <TableCell align="center">
                                                                {item.backInfo ? (
                                                                  <Tooltip title={`${item.backInfo.staffName}: ${item.backInfo.backRate}%`}>
                                                                    <Chip
                                                                      label={formatCurrency(item.backInfo.backAmount)}
                                                                      size="small"
                                                                      color="success"
                                                                      sx={{ height: 20 }}
                                                                    />
                                                                  </Tooltip>
                                                                ) : (
                                                                  <Typography variant="caption" color="textSecondary">-</Typography>
                                                                )}
                                                              </TableCell>
                                                              <TableCell align="center">
                                                                {!item.cancelInfo && (
                                                                  <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                                                                    <Tooltip title="バック確定">
                                                                      <IconButton
                                                                        size="small"
                                                                        color="primary"
                                                                        onClick={(e) => {
                                                                          e.stopPropagation();
                                                                          handleOpenItemBackDialog(order, idx);
                                                                        }}
                                                                      >
                                                                        <PersonIcon fontSize="small" />
                                                                      </IconButton>
                                                                    </Tooltip>
                                                                    {canDeleteOrders && (
                                                                    <Tooltip title="この商品を削除">
                                                                      <IconButton
                                                                        size="small"
                                                                        color="error"
                                                                        onMouseDown={(e) => {
                                                                          e.stopPropagation();
                                                                          handleDeleteLongPressStart(order, idx, false);
                                                                        }}
                                                                        onMouseUp={(e) => {
                                                                          e.stopPropagation();
                                                                          handleDeleteLongPressEnd();
                                                                        }}
                                                                        onMouseLeave={handleDeleteLongPressEnd}
                                                                        onTouchStart={(e) => {
                                                                          e.stopPropagation();
                                                                          handleDeleteLongPressStart(order, idx, false);
                                                                        }}
                                                                        onTouchEnd={(e) => {
                                                                          e.stopPropagation();
                                                                          handleDeleteLongPressEnd();
                                                                        }}
                                                                        sx={{
                                                                          transition: 'all 0.3s',
                                                                          '&:active': {
                                                                            backgroundColor: 'error.main',
                                                                            color: 'white',
                                                                          },
                                                                        }}
                                                                      >
                                                                        <DeleteIcon fontSize="small" />
                                                                      </IconButton>
                                                                    </Tooltip>
                                                                    )}
                                                                  </Box>
                                                                )}
                                                              </TableCell>
                                                            </TableRow>
                                                          ))}
                                                        </TableBody>
                                                      </Table>

                                                      {/* 販売スタッフ・バック情報（原価バック機能が有効な場合のみ） */}
                                                      {shop?.costBackSettings?.enabled && order.salesStaff && order.salesStaff.length > 0 && (
                                                        <Box sx={{ mt: 2, p: 1.5, backgroundColor: 'primary.50', borderRadius: 1 }}>
                                                          <Typography variant="caption" color="primary.main" sx={{ fontWeight: 'bold' }}>
                                                            販売スタッフ・バック情報
                                                          </Typography>
                                                          <Grid container spacing={1} sx={{ mt: 0.5 }}>
                                                            {order.salesStaff.map((staff) => (
                                                              <Grid item xs={12} sm={6} key={staff.staffId}>
                                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                                  <Typography variant="body2">
                                                                    {staff.staffName} ({(staff.shareRate * 100).toFixed(0)}%)
                                                                  </Typography>
                                                                  <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                                                                    {formatCurrency(staff.backAmount ?? calculateStaffBackAmount(order, staff))}
                                                                  </Typography>
                                                                </Box>
                                                              </Grid>
                                                            ))}
                                                          </Grid>
                                                          <Divider sx={{ my: 1 }} />
                                                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                            <Typography variant="body2" color="textSecondary">合計バック</Typography>
                                                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                                              {formatCurrency(order.salesStaff.reduce((sum, s) =>
                                                                sum + (s.backAmount ?? calculateStaffBackAmount(order, s)), 0))}
                                                            </Typography>
                                                          </Box>
                                                        </Box>
                                                      )}
                                                    </Box>
                                                  </Collapse>
                                                </TableCell>
                                              </TableRow>
                                            </React.Fragment>
                                          ))}
                                        </TableBody>
                                      </Table>
                                    )}
                                    <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                                      会計ID: {checkout.id.slice(0, 8)}...
                                    </Typography>
                                  </Box>
                                </Collapse>
                              </TableCell>
                            </TableRow>
                          </React.Fragment>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : getOrdersForDate(selectedDate).length > 0 ? (
                /* 会計データがない場合: 従来の注文直接表示 */
                <Box>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    会計データがないため、注文を直接表示しています
                  </Alert>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell width={50}></TableCell>
                          <TableCell>時刻</TableCell>
                          <TableCell>顧客</TableCell>
                          <TableCell>テーブル</TableCell>
                          <TableCell>ステータス</TableCell>
                          <TableCell align="right">金額</TableCell>
                          <TableCell>支払方法</TableCell>
                          {canDeleteOrders && <TableCell width={60} align="center">削除</TableCell>}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {getOrdersForDate(selectedDate).map((order) => (
                          <React.Fragment key={order.id}>
                            <TableRow
                              hover
                              sx={{ cursor: 'pointer' }}
                              onClick={() => setExpandedOrderId(expandedOrderId === order.id ? null : order.id)}
                            >
                              <TableCell>
                                <IconButton size="small">
                                  {expandedOrderId === order.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                </IconButton>
                              </TableCell>
                              <TableCell>
                                {format(order.orderedAt, 'HH:mm')}
                                {(order as any).receiptIssued && (
                                  <Tooltip title="領収書発行済み（削除保護）">
                                    <Box component="span" sx={{ ml: 0.5, color: 'warning.main' }}>★</Box>
                                  </Tooltip>
                                )}
                              </TableCell>
                              <TableCell>
                                {(order.linePictureUrl || order.lineDisplayName || order.customerName || order.customerPhone) ? (
                                  <Tooltip title={order.lineDisplayName || order.customerName || order.customerPhone || ''}>
                                    <Avatar
                                      src={order.linePictureUrl}
                                      sx={{
                                        width: 32,
                                        height: 32,
                                        cursor: order.customerId ? 'pointer' : 'default',
                                        bgcolor: order.linePictureUrl ? 'transparent' : 'primary.main',
                                      }}
                                      onClick={(e) => {
                                        if (order.customerId) {
                                          e.stopPropagation();
                                          // basename は /staff-dashboard。先頭 / だけだと React Router に届かず404になる。
                                          window.open(
                                            `${process.env.PUBLIC_URL}/customers/${order.customerId}`,
                                            '_blank');
                                        }
                                      }}
                                    >
                                      {!order.linePictureUrl && (order.lineDisplayName?.[0] || order.customerName?.[0] || '?')}
                                    </Avatar>
                                  </Tooltip>
                                ) : (
                                  <Typography variant="caption" color="textSecondary">-</Typography>
                                )}
                              </TableCell>
                              <TableCell>{order.tableName || order.tableId || '-'}</TableCell>
                              <TableCell>
                                <Chip
                                  label={getStatusLabel(order.status)}
                                  color={getStatusColor(order.status)}
                                  size="small"
                                />
                                {order.salesStaff && order.salesStaff.length > 0 && (
                                  <Chip
                                    label={`${order.salesStaff.map(s => s.staffName).join(', ')}`}
                                    size="small"
                                    variant="outlined"
                                    sx={{ ml: 0.5 }}
                                  />
                                )}
                              </TableCell>
                              <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                                {formatCurrency(order.total)}
                              </TableCell>
                              <TableCell>{getPaymentMethodLabel(order.paymentMethod)}</TableCell>
                              <TableCell align="center">
                                {shop?.costBackSettings?.enabled && order.salesStaff && order.salesStaff.length > 0 && (
                                  <Tooltip title="バック率編集">
                                    <IconButton
                                      size="small"
                                      color="primary"
                                      onClick={(e) => handleOpenEditBackRateDialog(order, e)}
                                    >
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                {canDeleteOrders && (
                                <Tooltip title="この伝票を削除">
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={(e) => handleOpenDeleteDialog(order, e)}
                                    onMouseDown={(e) => handleOrderDeleteLongPressStart(order, e)}
                                    onMouseUp={handleOrderDeleteLongPressEnd}
                                    onMouseLeave={handleOrderDeleteLongPressEnd}
                                    onTouchStart={(e) => handleOrderDeleteLongPressStart(order, e)}
                                    onTouchEnd={handleOrderDeleteLongPressEnd}
                                    sx={{
                                      transition: 'all 0.3s',
                                      '&:active': {
                                        backgroundColor: 'error.main',
                                        color: 'white',
                                      },
                                    }}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                )}
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={8}>
                                <Collapse in={expandedOrderId === order.id} timeout="auto" unmountOnExit>
                                  <Box sx={{ py: 2, px: 2, backgroundColor: 'grey.50' }}>
                                    <Typography variant="subtitle2" gutterBottom>
                                      注文内容
                                    </Typography>
                                    <Table size="small">
                                      <TableHead>
                                        <TableRow>
                                          <TableCell>商品名</TableCell>
                                          <TableCell align="right">数量</TableCell>
                                          <TableCell align="right">単価</TableCell>
                                          <TableCell align="right">小計</TableCell>
                                          <TableCell align="center" width={140}>操作</TableCell>
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        {order.items.map((item, idx) => (
                                          <TableRow key={idx}>
                                            <TableCell>{item.productName}</TableCell>
                                            <TableCell align="right">{item.quantity}</TableCell>
                                            <TableCell align="right">
                                              {item.price ? formatCurrency(item.price) : '-'}
                                            </TableCell>
                                            <TableCell align="right">
                                              {item.subtotal ? formatCurrency(item.subtotal) : '-'}
                                            </TableCell>
                                            <TableCell align="center">
                                              <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                                                {shop?.costBackSettings?.enabled && (
                                                  <Tooltip title="バック確定">
                                                    <IconButton
                                                      size="small"
                                                      color="success"
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleOpenItemBackDialog(order, idx);
                                                      }}
                                                    >
                                                      <CheckCircleOutlineIcon fontSize="small" />
                                                    </IconButton>
                                                  </Tooltip>
                                                )}
                                                {canDeleteOrders && (
                                                <Tooltip title="この商品を削除">
                                                  <IconButton
                                                    size="small"
                                                    color="error"
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      handleOpenDeleteItemDialog(order, idx);
                                                    }}
                                                    onMouseDown={(e) => {
                                                      e.stopPropagation();
                                                      handleDeleteLongPressStart(order, idx, true);
                                                    }}
                                                    onMouseUp={handleDeleteLongPressEnd}
                                                    onMouseLeave={handleDeleteLongPressEnd}
                                                    onTouchStart={(e) => {
                                                      e.stopPropagation();
                                                      handleDeleteLongPressStart(order, idx, true);
                                                    }}
                                                    onTouchEnd={handleDeleteLongPressEnd}
                                                  >
                                                    <DeleteIcon fontSize="small" />
                                                  </IconButton>
                                                </Tooltip>
                                                )}
                                              </Box>
                                            </TableCell>
                                          </TableRow>
                                        ))}
                                      </TableBody>
                                    </Table>
                                    <Divider sx={{ my: 1 }} />
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                                      <Typography variant="body2" color="textSecondary">
                                        注文ID: {order.id.slice(0, 8)}...
                                      </Typography>
                                      <Box sx={{ textAlign: 'right' }}>
                                        {order.subtotal !== undefined && (
                                          <Typography variant="body2">小計: {formatCurrency(order.subtotal)}</Typography>
                                        )}
                                        {order.tax !== undefined && (
                                          <Typography variant="body2">税: {formatCurrency(order.tax)}</Typography>
                                        )}
                                        <Typography variant="body2" fontWeight="bold">
                                          合計: {formatCurrency(order.total)}
                                        </Typography>
                                      </Box>
                                    </Box>

                                    {/* 販売スタッフ・バック情報（原価バック機能が有効な場合のみ） */}
                                    {shop?.costBackSettings?.enabled && order.salesStaff && order.salesStaff.length > 0 && (
                                      <Box sx={{ mt: 2, p: 1.5, backgroundColor: 'primary.50', borderRadius: 1 }}>
                                        <Typography variant="caption" color="primary.main" sx={{ fontWeight: 'bold' }}>
                                          販売スタッフ・バック情報
                                        </Typography>
                                        <Grid container spacing={1} sx={{ mt: 0.5 }}>
                                          {order.salesStaff.map((staff) => (
                                            <Grid item xs={12} sm={6} key={staff.staffId}>
                                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <Typography variant="body2">
                                                  {staff.staffName} ({(staff.shareRate * 100).toFixed(0)}%)
                                                </Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                                                  {formatCurrency(staff.backAmount ?? calculateStaffBackAmount(order, staff))}
                                                </Typography>
                                              </Box>
                                            </Grid>
                                          ))}
                                        </Grid>
                                        <Divider sx={{ my: 1 }} />
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                          <Typography variant="body2" color="textSecondary">合計バック</Typography>
                                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                            {formatCurrency(order.salesStaff.reduce((sum, s) =>
                                              sum + (s.backAmount ?? calculateStaffBackAmount(order, s)), 0))}
                                          </Typography>
                                        </Box>
                                      </Box>
                                    )}
                                  </Box>
                                </Collapse>
                              </TableCell>
                            </TableRow>
                          </React.Fragment>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              ) : (
                <Box sx={{ py: 4, textAlign: 'center' }}>
                  <Typography color="textSecondary">この日の注文データはありません</Typography>
                </Box>
              )}

              {/* POS会計 */}
              {getPosCheckoutsForDate(selectedDate).length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                    POS会計
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>時刻</TableCell>
                          <TableCell>顧客</TableCell>
                          <TableCell>支払方法</TableCell>
                          <TableCell align="right">小計</TableCell>
                          <TableCell align="right">割引</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold' }}>合計</TableCell>
                          <TableCell>配分</TableCell>
                          {canDeleteOrders && <TableCell align="center">操作</TableCell>}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {getPosCheckoutsForDate(selectedDate).map((pos) => (
                          <TableRow key={pos.id} sx={{ opacity: pos.salesStatus === 'confirmed' ? 0.6 : 1 }}>
                            <TableCell>
                              {format(pos.createdAt, 'HH:mm')}
                              {pos.salesStatus === 'confirmed' && (
                                <Chip label="確定" size="small" color="default" sx={{ ml: 0.5, height: 18, fontSize: '0.65rem' }} />
                              )}
                              {pos.salesStatus === 'pending' && (
                                <Chip label="未確定" size="small" color="warning" sx={{ ml: 0.5, height: 18, fontSize: '0.65rem' }} />
                              )}
                            </TableCell>
                            <TableCell>{pos.customerName || '-'}</TableCell>
                            <TableCell>{getPaymentMethodLabel(pos.paymentMethod)}</TableCell>
                            <TableCell align="right">{formatCurrency(pos.subtotal)}</TableCell>
                            <TableCell align="right">
                              {pos.discount ? `-${formatCurrency(pos.discount.amount)}` : '-'}
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                              {formatCurrency(pos.total)}
                            </TableCell>
                            <TableCell>
                              {pos.salesAllocation ? (
                                <Box>
                                  {pos.salesAllocation.allocations.map((a, i) => (
                                    <Typography key={i} variant="caption" display="block">
                                      {a.staffName}: {formatCurrency(a.resolved)}
                                    </Typography>
                                  ))}
                                </Box>
                              ) : pos.salesStaff && pos.salesStaff.length > 0 ? (
                                <Chip label={pos.salesStaff.map(s => s.staffName).join(', ')} size="small" variant="outlined" />
                              ) : '-'}
                            </TableCell>
                            {canDeleteOrders && (
                              <TableCell align="center">
                                {pos.salesStatus === 'pending' && (
                                  <Tooltip title="売上確定">
                                    <IconButton size="small" color="success" onClick={() => handleOpenConfirmDialog('pos', pos.id)}>
                                      <CheckCircleOutlineIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                {pos.salesAllocation && pos.salesStatus !== 'confirmed' && (
                                  <Tooltip title="配分編集">
                                    <IconButton size="small" color="primary" onClick={() => handleOpenEditAllocationDialog('pos', pos.id)}>
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </TableCell>
                            )}
                          </TableRow>
                        ))}
                        <TableRow>
                          <TableCell colSpan={5} align="right" sx={{ fontWeight: 'bold' }}>
                            POS合計
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                            {formatCurrency(getPosCheckoutsForDate(selectedDate).reduce((sum, p) => sum + p.total, 0))}
                          </TableCell>
                          <TableCell colSpan={2} />
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDailyOrdersDialogOpen(false)}>閉じる</Button>
        </DialogActions>
      </Dialog>

      {/* 削除確認ダイアログ */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => !isDeleting && setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: 'error.main' }}>
          {checkoutToDelete ? '会計伝票削除の確認' : (isOrderHardDelete ? '⚠️ 注文の完全削除' : '注文削除の確認')}
        </DialogTitle>
        <DialogContent>
          {/* 会計削除の場合 */}
          {checkoutToDelete && (
            <Box>
              <Alert severity="error" sx={{ mb: 2 }}>
                この操作は取り消せません。会計伝票と紐づく全ての注文が完全に消去され、復元できません。
              </Alert>
              <Typography variant="body1" gutterBottom>
                以下の会計伝票を削除しますか？
              </Typography>
              <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">会計時刻</Typography>
                    <Typography>{format(checkoutToDelete.createdAt, 'HH:mm')}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">テーブル</Typography>
                    <Typography>{checkoutToDelete.tableName || checkoutToDelete.tableId || '-'}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">人数</Typography>
                    <Typography>{checkoutToDelete.guestCount || '-'}名</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">支払方法</Typography>
                    <Typography>{getPaymentMethodLabel(checkoutToDelete.paymentMethod)}</Typography>
                  </Grid>
                </Grid>
                <Divider sx={{ my: 1 }} />
                <Grid container spacing={1}>
                  <Grid item xs={4}>
                    <Typography variant="body2" color="textSecondary">小計</Typography>
                    <Typography>{formatCurrency(checkoutToDelete.subtotal)}</Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="body2" color="textSecondary">税</Typography>
                    <Typography>{formatCurrency(checkoutToDelete.tax)}</Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="body2" color="textSecondary">合計</Typography>
                    <Typography sx={{ fontWeight: 'bold' }}>{formatCurrency(checkoutToDelete.total)}</Typography>
                  </Grid>
                </Grid>
                <Divider sx={{ my: 1 }} />
                <Typography variant="body2" color="textSecondary">
                  紐づく注文: {getOrdersForCheckout(checkoutToDelete).length}件
                </Typography>
              </Paper>
            </Box>
          )}
          {/* 注文削除の場合 */}
          {orderToDelete && (
            <Box>
              <Alert severity={isOrderHardDelete ? 'error' : 'warning'} sx={{ mb: 2 }}>
                {isOrderHardDelete
                  ? '⚠️ 長押しによる完全削除モードです。この操作は取り消せません。削除した注文は完全に消去され、復元できません。'
                  : 'この注文をキャンセル（マイナス伝票）として処理します。売上集計から除外されますが、履歴は残ります。'}
              </Alert>
              <Typography variant="body1" gutterBottom>
                以下の注文を{isOrderHardDelete ? '完全に削除' : 'キャンセル'}しますか？
              </Typography>
              <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">時刻</Typography>
                    <Typography>{format(orderToDelete.orderedAt, 'HH:mm')}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">テーブル</Typography>
                    <Typography>{orderToDelete.tableName || orderToDelete.tableId || '-'}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">ステータス</Typography>
                    <Chip
                      label={getStatusLabel(orderToDelete.status)}
                      color={getStatusColor(orderToDelete.status)}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">金額</Typography>
                    <Typography sx={{ fontWeight: 'bold' }}>{formatCurrency(orderToDelete.total)}</Typography>
                  </Grid>
                </Grid>
                <Divider sx={{ my: 1 }} />
                <Typography variant="body2" color="textSecondary">注文内容</Typography>
                {orderToDelete.items.map((item, idx) => (
                  <Typography key={idx} variant="body2">
                    {item.productName} × {item.quantity}
                  </Typography>
                ))}
              </Paper>
              {/* 通常削除時は理由入力欄を表示 */}
              {!isOrderHardDelete && (
                <TextField
                  fullWidth
                  label="キャンセル理由"
                  placeholder="例: お客様都合、注文間違い、品切れ等"
                  value={orderDeleteReason}
                  onChange={(e) => setOrderDeleteReason(e.target.value)}
                  sx={{ mt: 2 }}
                  multiline
                  rows={2}
                />
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDeleteDialogOpen(false);
              setOrderToDelete(null);
              setCheckoutToDelete(null);
              setIsOrderHardDelete(false);
              setOrderDeleteReason('');
            }}
            disabled={isDeleting}
          >
            閉じる
          </Button>
          <Button
            onClick={() => {
              // 会計伝票の取消は、記録つきのモーダルへ回す。
              // 「番号がどうなるか」「バックがどう動くか」を見せてから消す。
              if (checkoutToDelete) {
                const orders = getOrdersForCheckout(checkoutToDelete);
                setVoidTarget({
                  id: checkoutToDelete.id,
                  type: 'checkout',
                  slipNumber: (checkoutToDelete as any).slipNumber ?? null,
                  total: checkoutToDelete.total ?? 0,
                  itemCount: orders.reduce((n, o: any) => n + (o.items?.length ?? 0), 0),
                  businessDate: (checkoutToDelete as any).businessDate ?? '',
                  checkoutAt: checkoutToDelete.createdAt,
                  receiptIssued: orders.some((o: any) => o.receiptIssuedAt || o.receiptNumber),
                  afterDailyClose: false,
                  backAmount: 0,
                });
                setDeleteDialogOpen(false);
                return;
              }
              if (isOrderHardDelete) {
                setPendingHardDelete(() => handleDeleteOrder);
                setHardDeleteConfirmOpen(true);
              } else {
                handleDeleteOrder();
              }
            }}
            color="error"
            variant="contained"
            disabled={isDeleting}
            startIcon={isDeleting ? <CircularProgress size={20} /> : (isOrderHardDelete ? <DeleteIcon /> : <CancelIcon />)}
          >
            {isDeleting
              ? '処理中...'
              : checkoutToDelete
                ? '完全に削除'
                : isOrderHardDelete
                  ? '完全に削除'
                  : 'キャンセル処理'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* バック率編集ダイアログ */}
      <Dialog
        open={editBackRateDialogOpen}
        onClose={() => !isSavingBackRate && setEditBackRateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          バック率編集
        </DialogTitle>
        <DialogContent>
          {editingOrder && (
            <Box>
              <Paper variant="outlined" sx={{ p: 2, mb: 2, backgroundColor: 'grey.50' }}>
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">注文日時</Typography>
                    <Typography>{format(editingOrder.orderedAt, 'yyyy/MM/dd HH:mm')}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">テーブル</Typography>
                    <Typography>{editingOrder.tableName || editingOrder.tableNumber || editingOrder.tableId || '-'}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2" color="textSecondary">合計金額</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                      {formatCurrency(editingOrder.total)}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>

              {editingSalesStaff.length === 0 ? (
                <Alert severity="info">
                  この注文には販売スタッフが設定されていません
                </Alert>
              ) : (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>販売スタッフ別バック率</Typography>
                  {editingSalesStaff.map((staff, index) => (
                    <Paper key={staff.staffId} variant="outlined" sx={{ p: 2, mb: 1 }}>
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={4}>
                          <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                            {staff.staffName}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            分配率: {(staff.shareRate * 100).toFixed(0)}%
                          </Typography>
                        </Grid>
                        <Grid item xs={6} sm={4}>
                          <TextField
                            label="バック率 (%)"
                            type="number"
                            size="small"
                            fullWidth
                            value={staff.backRate !== undefined ? (staff.backRate * 100).toFixed(0) : (shop?.costBackSettings?.defaultBackRate ? (shop.costBackSettings.defaultBackRate * 100).toFixed(0) : '50')}
                            onChange={(e) => {
                              const newRate = parseFloat(e.target.value) / 100;
                              setEditingSalesStaff(prev => prev.map((s, i) =>
                                i === index ? { ...s, backRate: newRate } : s
                              ));
                            }}
                            inputProps={{ min: 0, max: 100, step: 1 }}
                          />
                        </Grid>
                        <Grid item xs={6} sm={4}>
                          <Typography variant="body2" color="textSecondary">バック金額</Typography>
                          <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                            {formatCurrency(calculateStaffBackAmount(editingOrder, {
                              ...staff,
                              backRate: staff.backRate ?? shop?.costBackSettings?.defaultBackRate ?? 0.5,
                            }))}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Paper>
                  ))}

                  {/* 合計バック金額 */}
                  <Paper sx={{ p: 2, mt: 2, backgroundColor: 'primary.50' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body1">合計バック金額</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                        {formatCurrency(
                          editingSalesStaff.reduce((sum, staff) => {
                            return sum + calculateStaffBackAmount(editingOrder, {
                              ...staff,
                              backRate: staff.backRate ?? shop?.costBackSettings?.defaultBackRate ?? 0.5,
                            });
                          }, 0)
                        )}
                      </Typography>
                    </Box>
                  </Paper>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setEditBackRateDialogOpen(false);
              setEditingOrder(null);
              setEditingSalesStaff([]);
            }}
            disabled={isSavingBackRate}
          >
            キャンセル
          </Button>
          <Button
            onClick={handleSaveBackRate}
            variant="contained"
            disabled={isSavingBackRate || editingSalesStaff.length === 0}
            startIcon={isSavingBackRate ? <CircularProgress size={20} /> : null}
          >
            {isSavingBackRate ? '保存中...' : '保存'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 商品単位バック確定ダイアログ */}
      <Dialog
        open={editItemBackDialogOpen}
        onClose={() => setEditItemBackDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          商品バック確定
        </DialogTitle>
        <DialogContent>
          {editingItemOrder && editingItemIndex >= 0 && (
            <Box sx={{ pt: 1 }}>
              <Paper variant="outlined" sx={{ p: 2, mb: 2, backgroundColor: 'grey.50' }}>
                <Typography variant="body2" color="textSecondary">対象商品</Typography>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  {editingItemOrder.items[editingItemIndex]?.productName}
                </Typography>
                <Typography variant="body1" sx={{ mt: 1 }}>
                  数量: {editingItemOrder.items[editingItemIndex]?.quantity}個 /
                  小計: {formatCurrency(
                    editingItemOrder.items[editingItemIndex]?.subtotal ||
                    (editingItemOrder.items[editingItemIndex]?.price || 0) * editingItemOrder.items[editingItemIndex]?.quantity
                  )}
                </Typography>
              </Paper>

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>担当スタッフ</InputLabel>
                <Select
                  value={editingItemBackInfo.staffId}
                  label="担当スタッフ"
                  onChange={(e) => {
                    const selectedStaff = staff.find(s => s.id === e.target.value);
                    setEditingItemBackInfo(prev => ({
                      ...prev,
                      staffId: e.target.value,
                      staffName: selectedStaff?.name || '',
                    }));
                  }}
                >
                  {staff.map((s) => (
                    <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                label="バック率 (%)"
                type="number"
                fullWidth
                sx={{ mb: 2 }}
                value={editingItemBackInfo.backRate}
                onChange={(e) => {
                  const value = Math.max(0, Math.min(100, parseFloat(e.target.value) || 0));
                  setEditingItemBackInfo(prev => ({ ...prev, backRate: value }));
                }}
                inputProps={{ min: 0, max: 100, step: 1 }}
              />

              {editingItemBackInfo.staffId && (
                <Paper sx={{ p: 2, backgroundColor: 'success.50' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body1">バック金額</Typography>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                      {formatCurrency(
                        Math.round(
                          (editingItemOrder.items[editingItemIndex]?.subtotal ||
                            (editingItemOrder.items[editingItemIndex]?.price || 0) * editingItemOrder.items[editingItemIndex]?.quantity) *
                          editingItemBackInfo.backRate / 100
                        )
                      )}
                    </Typography>
                  </Box>
                </Paper>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setEditItemBackDialogOpen(false);
            setEditingItemOrder(null);
            setEditingItemIndex(-1);
          }}>
            キャンセル
          </Button>
          <Button
            onClick={handleSaveItemBack}
            variant="contained"
            color="primary"
            disabled={!editingItemBackInfo.staffId}
          >
            確定
          </Button>
        </DialogActions>
      </Dialog>

      {/* 売上確定ダイアログ */}
      <Dialog
        open={confirmDialogOpen}
        onClose={() => !isConfirming && setConfirmDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>売上確定</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mt: 1 }}>
            この伝票の売上配分を確定します。確定後は配分の編集ができなくなります。
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setConfirmDialogOpen(false); setConfirmTarget(null); }} disabled={isConfirming}>
            キャンセル
          </Button>
          <Button
            onClick={handleConfirmSales}
            variant="contained"
            color="success"
            disabled={isConfirming}
            startIcon={isConfirming ? <CircularProgress size={20} /> : <CheckCircleOutlineIcon />}
          >
            {isConfirming ? '確定中...' : '確定する'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 配分編集ダイアログ */}
      <Dialog
        open={editAllocationDialogOpen}
        onClose={() => !isSavingAllocation && setEditAllocationDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>売上配分編集</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Paper variant="outlined" sx={{ p: 2, mb: 2, backgroundColor: 'grey.50' }}>
              <Typography variant="body2" color="textSecondary">純売上（原価控除後）</Typography>
              <Typography variant="h6" fontWeight="bold">{formatCurrency(editAllocNetSales)}</Typography>
            </Paper>

            {editAllocations.map((alloc, index) => (
              <Paper key={index} variant="outlined" sx={{ p: 2, mb: 1 }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={3}>
                    <Typography variant="body1" fontWeight="medium">{alloc.staffName}</Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <FormControl size="small" fullWidth>
                      <InputLabel>種類</InputLabel>
                      <Select
                        value={alloc.type}
                        label="種類"
                        onChange={(e) => {
                          const newAllocs = [...editAllocations];
                          newAllocs[index] = { ...newAllocs[index], type: e.target.value as 'percent' | 'amount' };
                          setEditAllocations(recalcAllocations(newAllocs, editAllocNetSales));
                        }}
                      >
                        <MenuItem value="percent">%</MenuItem>
                        <MenuItem value="amount">金額</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <TextField
                      label={alloc.type === 'percent' ? '割合 (%)' : '金額'}
                      type="number"
                      size="small"
                      fullWidth
                      value={alloc.value}
                      onChange={(e) => {
                        const newAllocs = [...editAllocations];
                        newAllocs[index] = { ...newAllocs[index], value: parseFloat(e.target.value) || 0 };
                        setEditAllocations(recalcAllocations(newAllocs, editAllocNetSales));
                      }}
                      inputProps={{ min: 0 }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <Typography variant="body2" color="textSecondary">配分額</Typography>
                    <Typography variant="body1" fontWeight="bold" color="primary.main">
                      {formatCurrency(alloc.resolved)}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
            ))}

            {/* 合計チェック */}
            <Paper sx={{ p: 2, mt: 2, backgroundColor: (() => {
              const total = editAllocations.reduce((sum, a) => sum + a.resolved, 0);
              return Math.abs(total - editAllocNetSales) <= 1 ? 'success.50' : 'error.50';
            })() }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body1">配分合計</Typography>
                <Typography variant="h6" fontWeight="bold">
                  {formatCurrency(editAllocations.reduce((sum, a) => sum + a.resolved, 0))}
                  {' / '}
                  {formatCurrency(editAllocNetSales)}
                </Typography>
              </Box>
              {Math.abs(editAllocations.reduce((sum, a) => sum + a.resolved, 0) - editAllocNetSales) > 1 && (
                <Typography variant="caption" color="error">
                  配分合計が純売上と一致していません
                </Typography>
              )}
            </Paper>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => { setEditAllocationDialogOpen(false); setEditAllocTarget(null); }}
            disabled={isSavingAllocation}
          >
            キャンセル
          </Button>
          <Button
            onClick={handleSaveAllocation}
            variant="contained"
            disabled={isSavingAllocation || Math.abs(editAllocations.reduce((sum, a) => sum + a.resolved, 0) - editAllocNetSales) > 1}
            startIcon={isSavingAllocation ? <CircularProgress size={20} /> : null}
          >
            {isSavingAllocation ? '保存中...' : '保存'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 商品削除ダイアログ */}
      <Dialog
        open={deleteItemDialogOpen}
        onClose={() => setDeleteItemDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: isHardDelete ? 'error.main' : 'inherit' }}>
          {isHardDelete ? '⚠️ 商品の完全削除' : '商品の削除'}
        </DialogTitle>
        <DialogContent>
          {deleteItemOrder && deleteItemIndex >= 0 && (
            <Box sx={{ pt: 1 }}>
              <Paper variant="outlined" sx={{ p: 2, mb: 2, backgroundColor: isHardDelete ? 'error.50' : 'grey.50' }}>
                <Typography variant="body2" color="textSecondary">削除対象商品</Typography>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  {deleteItemOrder.items[deleteItemIndex]?.productName}
                </Typography>
                <Typography variant="body1" sx={{ mt: 1 }}>
                  数量: {deleteItemOrder.items[deleteItemIndex]?.quantity}個 /
                  小計: {formatCurrency(
                    deleteItemOrder.items[deleteItemIndex]?.subtotal ||
                    (deleteItemOrder.items[deleteItemIndex]?.price || 0) * deleteItemOrder.items[deleteItemIndex]?.quantity
                  )}
                </Typography>
              </Paper>

              {isHardDelete ? (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                    完全削除を実行します
                  </Typography>
                  <Typography variant="body2">
                    この操作は取り消せません。履歴も残りません。
                  </Typography>
                </Alert>
              ) : (
                <TextField
                  label="削除理由"
                  fullWidth
                  multiline
                  rows={2}
                  value={deleteItemReason}
                  onChange={(e) => setDeleteItemReason(e.target.value)}
                  placeholder="例: お客様からのキャンセル要望、誤注文など"
                  required
                />
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setDeleteItemDialogOpen(false);
            setDeleteItemOrder(null);
            setDeleteItemIndex(-1);
            setDeleteItemReason('');
            setIsHardDelete(false);
          }}>
            キャンセル
          </Button>
          <Button
            onClick={() => {
              if (isHardDelete) {
                setPendingHardDelete(() => handleDeleteItem);
                setHardDeleteConfirmOpen(true);
              } else {
                handleDeleteItem();
              }
            }}
            variant="contained"
            color="error"
            disabled={!isHardDelete && !deleteItemReason.trim()}
          >
            {isHardDelete ? '完全に削除' : '削除（履歴あり）'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 完全削除の最終確認モーダル（二段階確認） */}
      <Dialog
        open={hardDeleteConfirmOpen}
        onClose={() => !isDeleting && setHardDeleteConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ color: 'error.main' }}>⚠️ 最終確認</DialogTitle>
        <DialogContent>
          <Alert severity="error">
            この操作は取り消せません。データは完全に削除され、履歴も残りません。本当に実行しますか？
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setHardDeleteConfirmOpen(false);
              setPendingHardDelete(null);
            }}
            disabled={isDeleting}
          >
            戻る
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={isDeleting || !pendingHardDelete}
            startIcon={isDeleting ? <CircularProgress size={20} /> : <DeleteIcon />}
            onClick={async () => {
              if (!pendingHardDelete) return;
              try {
                await pendingHardDelete();
              } finally {
                setHardDeleteConfirmOpen(false);
                setPendingHardDelete(null);
              }
            }}
          >
            {isDeleting ? '削除中...' : 'はい、完全に削除する'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 伝票の取消（記録つき） */}
      <VoidSlipDialog
        open={!!voidTarget}
        onClose={() => setVoidTarget(null)}
        target={voidTarget}
        userRole={currentRole ?? ''}
        policy={(shop as any)?.voidPolicy}
        onConfirm={async ({ reasonCode, note }) => {
          await handleDeleteCheckout({ reasonCode, note });
          setVoidTarget(null);
        }}
      />
    </Box>
  );
};

export default Analytics;
